import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  TextField,
  Button,
  Stack,
  Typography,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider,
  Paper,
  InputAdornment,
  IconButton,
  Collapse,
  Radio,
  RadioGroup,
  FormLabel,
  Tooltip,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Autocomplete,
  Snackbar,
} from '@mui/material';
import { Iconify } from '../../components/iconify';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import moment from 'moment';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchPromotionById,
  updatePromotion,
  fetchProductsList,
  fetchCategories,
  fetchBrands,
  fetchPromotionChannels,
} from '../../../../../services/PromotionServices';
import '../../../../common/commonStyles.css';

// Import promotion type components
import BuyXGetYFree from './BuyXGetYFree';
import BuyXGetYDiscount from './BuyXGetYDiscount';
import BuyXDiscount from './BuyXDiscount';
import MultiLayerOffer from './MultiLayerOffer';
import GroupOffer from './GroupOffer';
import { getProductSelectPopupStock } from './promotionStockHelpers';
import {
  fromApiPromotionPricingType,
  normalizePieceBoxPricingSelect,
  resolveBuyProductPricingFields,
  resolveMultiLayerBuyPricingTypeFromApi,
  resolveMultiLayerGetPricingTypeFromApi,
} from './promotionPricingHelpers';

const parseNumber = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const parseInteger = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.trunc(num) : 0;
};

const parseNullableInteger = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.trunc(num) : null;
};

const normalizeDiscountType = (value) => {
  const normalized = (value || '').toString().trim().toLowerCase();
  if (['percentage', 'percent', 'percentage_value'].includes(normalized)) return 'percentage';
  if (['fixed', 'fixed_amount', 'fixed-amount', 'amount', 'value_off', 'value-off'].includes(normalized)) return 'fixed';
  if (['set_value', 'set-value'].includes(normalized)) return 'set-value';
  return normalized;
};

const normalizeItemDiscountType = (value, { allowNoDiscount = false } = {}) => {
  const normalized = normalizeDiscountType(value);
  if (normalized === 'percentage' || normalized === 'fixed') return normalized;
  return allowNoDiscount ? 'no-discount' : 'percentage';
};

const parsePriceWithFallback = (...values) => {
  let zeroCandidate = null;
  for (const value of values) {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      continue;
    }
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      if (parsed > 0) {
        return parsed;
      }
      if (parsed === 0 && zeroCandidate === null) {
        zeroCandidate = 0;
      }
    }
  }
  return zeroCandidate ?? 0;
};

const normalizeProductPricing = (product = {}) => ({
  salesPrice: parsePriceWithFallback(product.sales_price, product.box_sales_price, product.box_price, product.price),
  offerPrice: parsePriceWithFallback(product.offer_price, product.box_offer_price),
  costPrice: parsePriceWithFallback(product.cost_price, product.box_cost_price),
  boxSalesPrice: parsePriceWithFallback(product.box_sales_price, product.box_price, product.sales_price, product.price),
  boxOfferPrice: parsePriceWithFallback(product.box_offer_price, product.offer_price),
  boxCostPrice: parsePriceWithFallback(product.box_cost_price, product.cost_price),
  pieceSalesPrice: parsePriceWithFallback(
    product.piece_sales_price,
    product.piece_price,
    product.PiecePrice,
    product.sales_price,
    product.price
  ),
  pieceOfferPrice: parsePriceWithFallback(
    product.piece_offer_price,
    product.PiecePrice,
    product.offer_price,
    product.offerPiecePrice
  ),
  pieceCostPrice: parsePriceWithFallback(product.piece_cost_price, product.PiecePrice, product.cost_price),
});

const getProductStockValue = (product = {}) => {
  const stockCandidates = [
    product.stock,
    product.stock_qty,
    product.stock_quantity,
    product.stockQuantity,
    product.current_stock,
    product.currentStock,
    product.available_stock,
    product.availableStock,
    product.quantity_on_hand,
    product.quantityOnHand,
  ];

  for (const value of stockCandidates) {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      continue;
    }
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return 0;
};

const ProductSelectionModal = ({
  open,
  onClose,
  onConfirm,
  title,
  selectedProducts = [],
  productTypeFilter = null,
  showToast,
  alreadyAddedProducts = [],
  firstBuyProduct = null,
  isGetForSlab = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [brands, setBrands] = useState(['All']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentFirstProduct, setCurrentFirstProduct] = useState(null);

  useEffect(() => {
    if (open) {
      setSelectedItems(selectedProducts);
      setSearchQuery('');
      setDebouncedSearchQuery('');
      setSelectedCategory('All');
      setSelectedBrand('All');
      setCurrentPage(1);
      // Initialize currentFirstProduct from firstBuyProduct (from parent) or from selectedProducts
      if (firstBuyProduct) {
        console.log('MultiLayerOffer: Modal opened with firstBuyProduct from parent:', firstBuyProduct);
        setCurrentFirstProduct(firstBuyProduct);
      } else if (selectedProducts && selectedProducts.length > 0 && productTypeFilter) {
        console.log('MultiLayerOffer: Modal opened with selectedProducts, using first product:', selectedProducts[0]);
        setCurrentFirstProduct(selectedProducts[0]);
      } else {
        setCurrentFirstProduct(null);
      }
      loadCategories();
      loadBrands();
    }
  }, [open, selectedProducts, firstBuyProduct, productTypeFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Watch for when first product is selected/changed in modal (for MultiLayerOffer)
  // Skip this for get products (getForSlab) - no need to update currentFirstProduct or reload API
  useEffect(() => {
    if (open && productTypeFilter && !isGetForSlab) {
      if (selectedItems.length === 0) {
        // No products selected in modal - reset currentFirstProduct (but keep firstBuyProduct from parent)
        if (currentFirstProduct && !firstBuyProduct) {
          setCurrentFirstProduct(null);
          setCurrentPage(1);
        }
      } else if (selectedItems.length >= 1) {
        // First product selected - update currentFirstProduct and reload API
        const firstSelected = selectedItems[0];
        const currentProductId = currentFirstProduct ? (currentFirstProduct.product_id || currentFirstProduct.id) : null;
        const newProductId = firstSelected.product_id || firstSelected.id;

        // Update if it's a different product (different ID) or if currentFirstProduct is null
        if (!currentFirstProduct || currentProductId !== newProductId) {
          console.log('MultiLayerOffer: Updating currentFirstProduct from selectedItems:', firstSelected);
          setCurrentFirstProduct(firstSelected);
          // Reset to page 1 and reload with pack parameter
          setCurrentPage(1);
        }
      }
    }
  }, [selectedItems, open, productTypeFilter, currentFirstProduct, firstBuyProduct, isGetForSlab]);

  // Only include currentFirstProduct in dependency when not getForSlab
  const effectiveFirstProduct = useMemo(() => {
    return isGetForSlab ? null : currentFirstProduct;
  }, [isGetForSlab, currentFirstProduct]);

  useEffect(() => {
    if (open) {
      loadProducts();
    }
  }, [open, currentPage, rowsPerPage, debouncedSearchQuery, selectedCategory, selectedBrand, firstBuyProduct, effectiveFirstProduct]);

  const loadCategories = async () => {
    try {
      const response = await fetchCategories();
      if (response.success && response.data) {
        setCategories(['All', ...response.data.map((cat) => cat.name)]);
      }
    } catch (error) {
    }
  };

  const loadBrands = async () => {
    try {
      const response = await fetchBrands();
      if (response.success && response.data) {
        setBrands(['All', ...response.data.map((brand) => brand.name)]);
      }
    } catch (error) {
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        pageSize: rowsPerPage,
      };

      if (debouncedSearchQuery) {
        params.search = debouncedSearchQuery;
      }
      if (selectedCategory !== 'All') {
        params.category = selectedCategory;
      }
      if (selectedBrand !== 'All') {
        params.brand = selectedBrand;
      }

      if (productTypeFilter && productTypeFilter !== null && productTypeFilter !== '') {
        const productTypeMap = {
          'box-only': 'box',
          'piece-only': 'piece',
          'both': 'both'
        };
        params.producttype = productTypeMap[productTypeFilter] || productTypeFilter;
      }

      // For get products (getForSlab): Pass box parameter only when productTypeFilter is box-only
      if (isGetForSlab && productTypeFilter === 'box-only') {
        params.box = '1';
        console.log('Get Products: Adding box parameter to API call for box-only product type');
      } else {
        // Add pack parameter when first product is selected (ONLY for Multi-Layer Offer promotion buy products)
        // Use currentFirstProduct (from modal selection) or firstBuyProduct (from parent) - whichever exists
        const firstProduct = currentFirstProduct || firstBuyProduct;
        if (firstProduct) {
          // Try multiple possible property names for pack size
          const packSize = parseInt(
            firstProduct.pack_size ||
            firstProduct.pack ||
            firstProduct.unit_per_box ||
            firstProduct.unitPerBox ||
            firstProduct.size ||
            0,
            10
          );
          console.log('MultiLayerOffer: First product pack size check:', {
            product: firstProduct,
            pack_size: firstProduct.pack_size,
            pack: firstProduct.pack,
            unit_per_box: firstProduct.unit_per_box,
            unitPerBox: firstProduct.unitPerBox,
            size: firstProduct.size,
            calculatedPackSize: packSize
          });
          if (packSize > 0) {
            params.pack = packSize;
            console.log('MultiLayerOffer: Adding pack parameter to API call:', packSize);
          } else {
            console.warn('MultiLayerOffer: Pack size is 0 or not found in product:', firstProduct);
          }
        } else {
          console.log('MultiLayerOffer: No first product available (currentFirstProduct:', currentFirstProduct, ', firstBuyProduct:', firstBuyProduct, ')');
        }
      }

      console.log('API call params:', params);

      const response = await fetchProductsList(params);

      if (response.success && response.data) {
        let filteredProducts = response.data.products || [];

        if (productTypeFilter) {
          filteredProducts = filteredProducts.filter(product => {
            const split = product.split;
            if (productTypeFilter === 'box-only') {
              return split === 0 || split === 2;
            } else if (productTypeFilter === 'piece-only') {
              return split === 1 || split === 2;
            } else if (productTypeFilter === 'both') {
              return split === 0 || split === 1 || split === 2;
            }
            return true;
          });
        }

        // For Multi-Layer Offer: If first product is selected, filter by price only (pack is handled by API)
        // Skip price filtering for get products (getForSlab)
        if (firstBuyProduct && !isGetForSlab) {
          filteredProducts = filteredProducts.filter(product => {
            let priceMatch = false;

            if (productTypeFilter === 'box-only') {
              const productBoxPrice = parseFloat(product.salesPrice || product.box_sales_price || 0);
              const firstProductBoxPrice = parseFloat(firstBuyProduct.boxSalesPrice || firstBuyProduct.salesPrice || 0);
              priceMatch = productBoxPrice === firstProductBoxPrice;
            } else if (productTypeFilter === 'piece-only') {
              const productPiecePrice = parseFloat(product.piece_sales_price || product.PiecePrice || 0);
              const firstProductPiecePrice = parseFloat(firstBuyProduct.pieceSalesPrice || firstBuyProduct.piece_sales_price || 0);
              priceMatch = productPiecePrice === firstProductPiecePrice;
            } else if (productTypeFilter === 'both') {
              const productBoxPrice = parseFloat(product.salesPrice || product.box_sales_price || 0);
              const firstProductBoxPrice = parseFloat(firstBuyProduct.boxSalesPrice || firstBuyProduct.salesPrice || 0);
              const productPiecePrice = parseFloat(product.piece_sales_price || product.PiecePrice || 0);
              const firstProductPiecePrice = parseFloat(firstBuyProduct.pieceSalesPrice || firstBuyProduct.piece_sales_price || 0);
              priceMatch = (productBoxPrice === firstProductBoxPrice) && (productPiecePrice === firstProductPiecePrice);
            }

            return priceMatch;
          });
        }

        setProducts(filteredProducts);
        setPagination(response.data.pagination);
      } else {
        setError(response.message || 'Failed to load products');
        setProducts([]);
      }
    } catch (error) {
      setError('Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (product) => {
    setSelectedItems((prev) => {
      const productIdentifier = product.product_id || product.id;
      const isSelected = prev.some((item) => {
        const itemIdentifier = item.product_id || item.id;
        return itemIdentifier === productIdentifier;
      });
      if (isSelected) {
        return prev.filter((item) => {
          const itemIdentifier = item.product_id || item.id;
          return itemIdentifier !== productIdentifier;
        });
      } else {
        // Skip validation for get products (getForSlab)
        if (isGetForSlab) {
          return [...prev, product];
        }

        const baseline = prev[0] || firstBuyProduct;
        if (!productTypeFilter) {
          return [...prev, product];
        }
        if (productTypeFilter === 'box-only' && product.split === 1) {
          showToast('Selected product is piece-only. Please choose products that support boxes.', 'warning');
          return prev;
        }
        if (productTypeFilter === 'piece-only' && product.split === 0) {
          showToast('Selected product is box-only. Please choose products that support pieces.', 'warning');
          return prev;
        }
        if (baseline) {
          const packMatches = parseInteger(product.pack ?? product.pack_size ?? product.unit_per_box) === parseInteger(baseline.pack_size ?? baseline.pack ?? baseline.unit_per_box);
          const boxMatches = parseNumber(product.salesPrice ?? product.box_sales_price ?? product.price) === parseNumber(baseline.boxSalesPrice ?? baseline.salesPrice ?? baseline.box_sales_price ?? baseline.price);
          const pieceMatches = parseNumber(product.piece_sales_price ?? product.PiecePrice ?? product.price) === parseNumber(baseline.pieceSalesPrice ?? baseline.piece_sales_price ?? baseline.PiecePrice ?? baseline.price);
          const requireBoxMatch = productTypeFilter !== 'piece-only';
          const requirePieceMatch = productTypeFilter !== 'box-only';
          if (!packMatches || (requireBoxMatch && !boxMatches) || (requirePieceMatch && !pieceMatches)) {
            showToast('Selected product must match pack and price of the first product', 'warning');
            return prev;
          }
        }
        return [...prev, product];
      }
    });
  };

  const handleSelectAll = () => {
    const selectableProducts = products.filter((product) => {
      const productIdentifier = product.product_id || product.id;
      return !alreadyAddedProducts.some((item) => {
        const itemIdentifier = item.product_id || item.id;
        return itemIdentifier === productIdentifier;
      });
    });

    if (selectedItems.length === selectableProducts.length && selectableProducts.length > 0) {
      setSelectedItems([]);
    } else {
      setSelectedItems(selectableProducts);
    }
  };

  const handleConfirm = () => {
    onConfirm(selectedItems);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setCurrentPage(1);
  };

  const handleBrandChange = (event) => {
    setSelectedBrand(event.target.value);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadProducts();
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const selectableProducts = products.filter((product) => {
    const productIdentifier = product.product_id || product.id;
    return !alreadyAddedProducts.some((item) => {
      const itemIdentifier = item.product_id || item.id;
      return itemIdentifier === productIdentifier;
    });
  });

  const isAllSelected =
    selectableProducts.length > 0 &&
    selectableProducts.every((product) => {
      const productIdentifier = product.product_id || product.id;
      return selectedItems.some((item) => {
        const itemIdentifier = item.product_id || item.id;
        return itemIdentifier === productIdentifier;
      });
    });

  const isIndeterminate =
    selectableProducts.some((product) => {
      const productIdentifier = product.product_id || product.id;
      return selectedItems.some((item) => {
        const itemIdentifier = item.product_id || item.id;
        return itemIdentifier === productIdentifier;
      });
    }) && !isAllSelected;

  const totalCount = pagination?.total_count || 0;
  const startIndex = (currentPage - 1) * rowsPerPage + 1;
  const endIndex = Math.min(currentPage * rowsPerPage, totalCount);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{title}</Typography>
          <Button onClick={onClose} sx={{ minWidth: 'auto', p: 1 }}>
            <Iconify icon="eva:close-fill" />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: '70vh', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 3, pb: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search by product name or product code"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setTimeout(() => {
                  handleSearch();
                }, 100);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" />
                  </InputAdornment>
                ),
              }}
            />

            <Autocomplete
              sx={{ minWidth: 250 }}
              options={categories}
              value={selectedCategory}
              onChange={(event, newValue) => {
                handleCategoryChange({ target: { value: newValue || 'All' } });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Category" placeholder="Select Category" />
              )}
              isOptionEqualToValue={(option, value) => option === value}
            />

            <Autocomplete
              sx={{ minWidth: 250 }}
              options={brands}
              value={selectedBrand}
              onChange={(event, newValue) => {
                handleBrandChange({ target: { value: newValue || 'All' } });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Brand" placeholder="Select Brand" />
              )}
              isOptionEqualToValue={(option, value) => option === value}
            />
          </Box>
        </Box>

        {firstBuyProduct && (
          <Alert
            severity="info"
            sx={{
              mx: 3,
              mb: 2,
              borderRadius: 1,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Filtered by First Product Selection
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Showing only products with matching pack size ({firstBuyProduct.pack || firstBuyProduct.pack_size}) and price
              {productTypeFilter === 'box-only' && ` (Box: £${(firstBuyProduct.boxSalesPrice || firstBuyProduct.salesPrice || 0).toFixed(2)})`}
              {productTypeFilter === 'piece-only' && ` (Piece: £${(firstBuyProduct.pieceSalesPrice || firstBuyProduct.piece_sales_price || 0).toFixed(2)})`}
              {productTypeFilter === 'both' && ` (Box: £${(firstBuyProduct.boxSalesPrice || firstBuyProduct.salesPrice || 0).toFixed(2)}, Piece: £${(firstBuyProduct.pieceSalesPrice || firstBuyProduct.piece_sales_price || 0).toFixed(2)})`}
            </Typography>
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ flex: 1, overflowY: 'auto', mx: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                    disabled={loading || products.length === 0}
                    sx={{
                      color: '#2e7d32',
                      '&.Mui-checked': {
                        color: '#2e7d32',
                      },
                      '&.MuiCheckbox-indeterminate': {
                        color: '#2e7d32',
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} width="100px">Code</TableCell>
                <TableCell sx={{ fontWeight: 600 }} width="300px" >Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }} width="100px">Size</TableCell>
                <TableCell sx={{ fontWeight: 600 }} width="100px" align="right">Cost Price</TableCell>
                <TableCell sx={{ fontWeight: 600 }} width="100px" align="right">Sales Price</TableCell>
                <TableCell sx={{ fontWeight: 600 }} width="100px" align="right">Offer Price</TableCell>
                <TableCell sx={{ fontWeight: 600 }} width="100px">Margin</TableCell>
                <TableCell sx={{ fontWeight: 600 }} width="100px">Stock</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading products...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">
                      No products found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => {
                  const productIdentifier = product.product_id || product.id;
                  const isSelected = selectedItems.some((item) => {
                    const itemIdentifier = item.product_id || item.id;
                    return itemIdentifier === productIdentifier;
                  });
                  const isAlreadyAdded = alreadyAddedProducts.some((item) => {
                    const itemIdentifier = item.product_id || item.id;
                    return itemIdentifier === productIdentifier;
                  });
                  return (
                    <TableRow key={product.id} hover sx={{ opacity: isAlreadyAdded ? 0.5 : 1 }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleSelectProduct(product)}
                          disabled={isAlreadyAdded}
                          sx={{
                            color: '#2e7d32',
                            '&.Mui-checked': {
                              color: '#2e7d32',
                            },
                            '&.Mui-disabled': {
                              color: '#c0c0c0',
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>{product.code || product.product_code || product.id}</TableCell>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>{product.size}</TableCell>
                      <TableCell>£{(product.costPrice || 0).toFixed(2)}</TableCell>
                      <TableCell>£{(product.salesPrice || 0).toFixed(2)}</TableCell>
                      <TableCell>£{(product.offerPrice || 0).toFixed(2)}</TableCell>
                      <TableCell>{Math.round(product.margin || 0)}%</TableCell>
                      <TableCell>{getProductSelectPopupStock(product)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            p: 2,
            gap: 3,
            borderTop: '1px solid #e0e0e0',
            bgcolor: '#fafafa'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">Rows per page:</Typography>
            <Select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              size="small"
              sx={{ minWidth: 70 }}
              disabled={loading}
              MenuProps={{
                disablePortal: true,
                PaperProps: {
                  sx: {
                    maxHeight: '200px',
                    minHeight: '180px',
                    overflow: 'auto',
                    '& .MuiList-root': {
                      paddingTop: '8px',
                      paddingBottom: '8px',
                    },
                    '& .MuiMenuItem-root': {
                      fontSize: '14px',
                      minHeight: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      paddingTop: '5px',
                      paddingBottom: '10px',
                    }
                  }
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Box>

          <Typography variant="body2">
            {totalCount > 0 ? `${startIndex}-${endIndex} of ${totalCount}` : '0 of 0'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => handlePageChange(e, currentPage - 1)}
              disabled={currentPage === 1 || loading}
            >
              <Iconify icon="eva:arrow-ios-back-fill" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => handlePageChange(e, currentPage + 1)}
              disabled={currentPage >= Math.ceil(totalCount / rowsPerPage) || loading}
            >
              <Iconify icon="eva:arrow-ios-forward-fill" />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={selectedItems.length === 0}
          sx={{ backgroundColor: '#003DA5' }}
        >
          Confirm Selection ({selectedItems.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const promotionTypes = [
  {
    id: 'buy-x-get-y-free',
    title: 'Buy X Get Y Free',
    description: 'Buy a specified quantity of items and get items free from another product group.',
  },
  {
    id: 'buy-x-get-y-discount',
    title: 'Buy X Get Y at Discount',
    description: 'Buy specified items and get another product at discounted price or set value.',
  },
  {
    id: 'buy-x-discount',
    title: 'Buy X at Discount...',
    description: 'Buy specified items and get discount on their total value.',
  },
  {
    id: 'multi-layer-offer',
    title: 'Multi-Layer Special Offer',
    description: 'Setup multiple slab-based offers with escalating discounts for same products.',
  },
  {
    id: 'group-offer',
    title: 'Group Offer',
    description: 'Setup multiple slab-based offers with escalating discounts for same products.',
  },
];

export default function ViewPromotion() {
  const isViewMode = true;
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [promotionData, setPromotionData] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [showZeroPriceWarning, setShowZeroPriceWarning] = useState(false);
  const [zeroPriceProducts, setZeroPriceProducts] = useState([]);

  // Form data state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    promotionType: '',
    startDate: null,
    endDate: null,
    selectedChannels: [],
    buyProducts: [],
    getProducts: [],
    slabs: [],
    buyQuantity: 1,
    getQuantity: 1,
    buyGroupQuantities: {},
    getGroupQuantities: {},
    buyPricingType: 'sales',
    getPricingType: 'sales',
    // Buy X at Discount Configuration defaults
    buyXDiscountType: 'no-discount',
    buyXDiscountValue: '',
    buyXDiscountApplyTo: 'none',
    buyDiscountConfigType: 'value-off',
    // Buy X Get Y at Discount Configuration defaults
    getDiscountType: 'set-value',
    getSetValue: '',
    getItemDiscountType: 'percentage',
    getItemDiscountValue: '',
    getItemDiscountApplyTo: 'bulk',
    buyDiscountType: 'percentage',
    buyDiscountValue: '',
    buyDiscountApplyTo: 'none',
  });

  const [channels, setChannels] = useState([]);
  const [websiteChannelValue, setWebsiteChannelValue] = useState(null);
  const [mobileAppChannelValue, setMobileAppChannelValue] = useState(null);

  // Search states for product selection
  const [buyProductsSearch, setBuyProductsSearch] = useState('');
  const [getProductsSearch, setGetProductsSearch] = useState('');

  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [modalType, setModalType] = useState('buy');
  const [modalTitle, setModalTitle] = useState('');

  // Multi-layer specific states
  const [multiLayerStep, setMultiLayerStep] = useState(1);
  const [currentSlabIndex, setCurrentSlabIndex] = useState(null);

  // Error states
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadChannels();
    loadPromotionData();
  }, [id]);

  useEffect(() => {
    if (!isViewMode && formData.promotionType === 'multi-layer-offer' && formData.buyProducts.length > 0 && formData.slabs && formData.slabs.length > 0) {
      setMultiLayerStep(2);
    }
  }, [formData.promotionType, formData.buyProducts, formData.slabs]);

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast({ ...toast, open: false });
  };

  const loadChannels = async () => {
    try {
      const response = await fetchPromotionChannels();

      if (response.success && response.data) {
        const channelsArray = response.data.channels || response.data;

        const websiteChannel = Array.isArray(channelsArray)
          ? channelsArray.find((channel) => channel.is_active && channel.name === 'website')
          : null;
        if (websiteChannel) {
          setWebsiteChannelValue(websiteChannel.name);
        }

        const mobileAppChannel = Array.isArray(channelsArray)
          ? channelsArray.find((channel) => channel.is_active && (channel.name === 'mobile-app' || channel.display_name === 'Mobile Application'))
          : null;
        if (mobileAppChannel) {
          setMobileAppChannelValue(mobileAppChannel.name);
        }

        const channelsData = Array.isArray(channelsArray)
          ? channelsArray
            .filter((channel) => channel.is_active && channel.name !== 'website')
            .map((channel) => ({
              id: channel.id,
              value: channel.name,
              label:
                channel.name === 'mobile-app' || channel.display_name === 'Mobile Application'
                  ? 'Customer'
                  : channel.display_name,
              bitmap: channel.bitmap_value,
              isMobileApp: channel.name === 'mobile-app' || channel.display_name === 'Mobile Application',
            }))
          : [];

        setChannels(channelsData);
      } else {
      }
    } catch (error) {
    }
  };

  const loadPromotionData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetchPromotionById(id);

      if (response.success && response.data) {
        const data = response.data;
        setPromotionData(data);

        const rawBuyDiscountType =
          (data.buy_discount_type != null && String(data.buy_discount_type).trim() !== '')
            ? String(data.buy_discount_type).trim()
            : (data.buy_discount && data.buy_discount.discount_type != null && String(data.buy_discount.discount_type).trim() !== '')
              ? String(data.buy_discount.discount_type).trim()
              : '';

        const parsedChannels = data.channels
          ? data.channels.split(', ').map((channel) => {
            if (channel === 'Mobile Application' || channel === 'Mobile Application (Customer)' || channel === 'Customer') return 'mobile-app';
            if (channel === 'Website') return 'website';
            if (channel === 'Sales Representative') return 'sales-rep';
            if (channel === 'EPOS (Cash & Carry)') return 'epos';
            if (channel === 'Tele Calling') return 'tele-calling';
            return channel.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '');
          })
          : [];

        // Map promotion type from API to form format
        const mappedPromotionType = data.type
          ? (() => {
            switch (data.type) {
              case 'Buy X Get Y Free':
                return 'buy-x-get-y-free';
              case 'Buy X Get Y at Discount':
                return 'buy-x-get-y-discount';
              case 'Buy X at Discount':
              case 'Buy X at Discount...':
                return 'buy-x-discount';
              case 'Multi-Layer Special Offer':
              case 'Multi Layer Special Offer':
                return 'multi-layer-offer';
              case 'Group Offer':
                return 'group-offer';
              default:
                return data.type.toLowerCase().replace(/\s+/g, '-').replace('...', '');
            }
          })()
          : '';

        const mappedBuyPricingType =
          mappedPromotionType === 'multi-layer-offer'
            ? resolveMultiLayerBuyPricingTypeFromApi(data)
            : data.buy_pricing_type
              ? fromApiPromotionPricingType(data.buy_pricing_type, 'sales')
              : 'sales';

        const mappedGetPricingType =
          mappedPromotionType === 'multi-layer-offer'
            ? resolveMultiLayerGetPricingTypeFromApi(data)
            : data.get_pricing_type
              ? fromApiPromotionPricingType(data.get_pricing_type, 'sales')
              : 'sales';

        // Map buy products with proper structure
        const mappedBuyProducts = (data.buy_products || []).map((product, index) => {
          const pricing = normalizeProductPricing(product);
          const normalizedItemDiscountType = normalizeItemDiscountType(product.item_discount_type, { allowNoDiscount: true });
          const resolvedType = product.product_type || getDefaultProductType(parseInt(product.split) || 2);
          const pricingFields = resolveBuyProductPricingFields(product, mappedBuyPricingType);
          const pricingTypeForRow =
            resolvedType === 'Piece'
              ? pricingFields.piecePricingType
              : pricingFields.boxPricingType;
          const boxQuantity = parseNullableInteger(product.box_quantity);
          const pieceQuantity = parseNullableInteger(product.piece_quantity);
          const isCompulsory = product.is_compulsory === '1' || product.is_compulsory === 1;
          const compulsoryQty = isCompulsory ? parseInt(product.compulsory_quantity) : null;
          const resolvedQuantity = (() => {
            if (isCompulsory && compulsoryQty !== null && !isNaN(compulsoryQty)) {
              return compulsoryQty;
            }
            if (resolvedType === 'Piece') {
              if (pieceQuantity !== null) return pieceQuantity;
              if (boxQuantity !== null) return boxQuantity;
              return parseInt(product.quantity) || 1;
            }
            if (boxQuantity !== null) return boxQuantity;
            if (pieceQuantity !== null) return pieceQuantity;
            return parseInt(product.quantity) || 1;
          })();

          return {
            id: product.product_id,
            name: product.product_name,
            code: product.product_code,
            type: resolvedType,
            pricingType: pricingTypeForRow,
            boxPricingType: pricingFields.boxPricingType,
            piecePricingType: pricingFields.piecePricingType,
            compulsory: isCompulsory,
            quantity: resolvedQuantity,
            group_quantity: (() => {
              const raw = product.group_quantity ?? product.groupQuantity ?? product.quantity;
              const n = parseInt(raw, 10);
              return Number.isFinite(n) && n > 0 ? n : 0;
            })(),
            stockDisplay: resolvedQuantity,
            boxQuantity,
            pieceQuantity,
            originalIndex: index,
            // Map pricing data directly from API response
            costPrice: pricing.costPrice,
            salesPrice: pricing.salesPrice,
            offerPrice: pricing.offerPrice,
            boxCostPrice: pricing.boxCostPrice,
            boxSalesPrice: pricing.boxSalesPrice,
            boxOfferPrice: pricing.boxOfferPrice,
            pieceCostPrice: pricing.pieceCostPrice,
            pieceSalesPrice: pricing.pieceSalesPrice,
            pieceOfferPrice: pricing.pieceOfferPrice,
            split: parseInt(product.split) || 2,
            pack_size: parseInt(product.pack) || 1,
            // Stock field - may not be available in this API response
            stock: getProductStockValue(product),
            // Add default size if not available
            size: product.size || 'N/A',
            // Map discount fields for Buy X at Discount
            buyXItemDiscountType: normalizedItemDiscountType,
            buyXItemDiscountValue: product.item_discount_value || '',
            // Map discount fields for Buy X Get Y at Discount (buy products)
            buyItemDiscountType: normalizeItemDiscountType(product.item_discount_type) || 'percentage',
            buyItemDiscountValue: product.item_discount_value || '',
            groupNo:
              product.group_id != null && String(product.group_id).trim() !== ''
                ? String(product.group_id)
                : product.group_no != null && String(product.group_no).trim() !== ''
                  ? String(product.group_no)
                  : '',
          };
        });

        // Map get products with proper structure
        const mappedGetProducts = (data.get_products || []).map((product, index) => {
          const pricing = normalizeProductPricing(product);
          const resolvedType = product.product_type || getDefaultProductType(parseInt(product.split) || 2);
          const boxQuantity = parseNullableInteger(product.box_quantity);
          const pieceQuantity = parseNullableInteger(product.piece_quantity);
          const isCompulsory = product.is_compulsory === '1' || product.is_compulsory === 1;
          const compulsoryQty = isCompulsory ? parseInt(product.compulsory_quantity) : null;
          const resolvedQuantity = (() => {
            if (isCompulsory && compulsoryQty !== null && !isNaN(compulsoryQty)) {
              return compulsoryQty;
            }
            if (resolvedType === 'Piece') {
              if (pieceQuantity !== null) return pieceQuantity;
              if (boxQuantity !== null) return boxQuantity;
              return parseInt(product.quantity) || 1;
            }
            if (boxQuantity !== null) return boxQuantity;
            if (pieceQuantity !== null) return pieceQuantity;
            return parseInt(product.quantity) || 1;
          })();

          return {
            id: product.product_id,
            name: product.product_name,
            code: product.product_code,
            type: resolvedType,
            pricingType: normalizePieceBoxPricingSelect(
              product.box_pricing_type || product.pricing_type,
              mappedGetPricingType
            ),
            boxPricingType: normalizePieceBoxPricingSelect(product.box_pricing_type, mappedGetPricingType),
            piecePricingType: normalizePieceBoxPricingSelect(product.piece_pricing_type, mappedGetPricingType),
            compulsory: isCompulsory,
            quantity: resolvedQuantity,
            group_quantity: (() => {
              const raw = product.group_quantity ?? product.groupQuantity ?? product.quantity;
              const n = parseInt(raw, 10);
              return Number.isFinite(n) && n > 0 ? n : 0;
            })(),
            stockDisplay: resolvedQuantity,
            boxQuantity,
            pieceQuantity,
            originalIndex: index,
            // Map pricing data directly from API response
            costPrice: pricing.costPrice,
            salesPrice: pricing.salesPrice,
            offerPrice: pricing.offerPrice,
            boxCostPrice: pricing.boxCostPrice,
            boxSalesPrice: pricing.boxSalesPrice,
            boxOfferPrice: pricing.boxOfferPrice,
            pieceCostPrice: pricing.pieceCostPrice,
            pieceSalesPrice: pricing.pieceSalesPrice,
            pieceOfferPrice: pricing.pieceOfferPrice,
            split: parseInt(product.split) || 2,
            pack_size: parseInt(product.pack) || 1,
            // Stock field - may not be available in this API response
            stock: getProductStockValue(product),
            // Add default size if not available
            size: product.size || 'N/A',
            // Map discount fields for Buy X Get Y at Discount (get products)
            itemDiscountType: normalizeItemDiscountType(product.item_discount_type) || 'percentage',
            itemDiscountValue: product.item_discount_value || '',
            groupNo:
              product.group_id != null && String(product.group_id).trim() !== ''
                ? String(product.group_id)
                : product.group_no != null && String(product.group_no).trim() !== ''
                  ? String(product.group_no)
                  : '',
          };
        });

        // Fallback: some multi-layer payloads provide buy products only inside slabs
        if (mappedPromotionType === 'multi-layer-offer' && mappedBuyProducts.length === 0 && Array.isArray(data.slabs)) {
          const slabBuy = [];
          (data.slabs || []).forEach((slab) => {
            (slab.buy_products || []).forEach((product, index) => {
              const normalized = normalizeProductPricing(product);
              const resolvedType = product.product_type || getDefaultProductType(parseInt(product.split) || 2);
              const boxQuantity = parseNullableInteger(product.box_quantity);
              const pieceQuantity = parseNullableInteger(product.piece_quantity);
              const fallbackQuantity = parseInt(product.quantity) || parseInt(product.compulsory_quantity) || 1;
              const resolvedQuantity = (() => {
                if (resolvedType === 'Piece') {
                  if (pieceQuantity !== null) return pieceQuantity;
                  if (boxQuantity !== null) return boxQuantity;
                  return fallbackQuantity;
                }
                if (boxQuantity !== null) return boxQuantity;
                if (pieceQuantity !== null) return pieceQuantity;
                return fallbackQuantity;
              })();

              const pricingFields = resolveBuyProductPricingFields(product, mappedBuyPricingType);
              slabBuy.push({
                id: product.product_id,
                name: product.product_name,
                code: product.product_code,
                type: resolvedType,
                pricingType:
                  resolvedType === 'Piece'
                    ? pricingFields.piecePricingType
                    : pricingFields.boxPricingType,
                boxPricingType: pricingFields.boxPricingType,
                piecePricingType: pricingFields.piecePricingType,
                compulsory: product.is_compulsory === '1' || product.is_compulsory === 1,
                quantity: resolvedQuantity,
                stockDisplay: resolvedQuantity,
                boxQuantity,
                pieceQuantity,
                originalIndex: slabBuy.length,
                costPrice: normalized.costPrice,
                salesPrice: normalized.boxSalesPrice,
                offerPrice: normalized.boxOfferPrice,
                boxCostPrice: normalized.boxCostPrice,
                boxSalesPrice: normalized.boxSalesPrice,
                boxOfferPrice: normalized.boxOfferPrice,
                pieceCostPrice: normalized.pieceCostPrice,
                pieceSalesPrice: normalized.pieceSalesPrice,
                pieceOfferPrice: normalized.pieceOfferPrice,
                split: parseInt(product.split) || 2,
                pack_size: parseInt(product.pack) || 1,
                stock: getProductStockValue(product),
                size: product.size || 'N/A',
                buyXItemDiscountType: normalizeItemDiscountType(product.item_discount_type, { allowNoDiscount: true }),
                buyXItemDiscountValue: product.item_discount_value || '',
                buyItemDiscountType: normalizeItemDiscountType(product.item_discount_type) || 'percentage',
                buyItemDiscountValue: product.item_discount_value || '',
              });
            });
          });
          if (slabBuy.length > 0) {
            mappedBuyProducts.splice(0, mappedBuyProducts.length, ...slabBuy);
          }
        }

        let finalBuyPricingType = mappedBuyPricingType;
        if (
          mappedPromotionType === 'multi-layer-offer' &&
          mappedBuyProducts.some((product) => product.boxPricingType === 'offer' || product.pricingType === 'offer')
        ) {
          finalBuyPricingType = 'offer';
        }

        // Determine buyProductType for multi-layer offers based on product types
        let buyProductType = 'box-only';
        if (mappedPromotionType === 'multi-layer-offer' && mappedBuyProducts.length > 0) {
          const hasBox = mappedBuyProducts.some(p => p.type === 'Box' || p.type === 'Both');
          const hasPiece = mappedBuyProducts.some(p => p.type === 'Piece' || p.type === 'Both');
          if (hasBox && !hasPiece) buyProductType = 'box-only';
          else if (hasPiece && !hasBox) buyProductType = 'piece-only';
          else buyProductType = 'both';
        }

        // Map slabs data for multi-layer offers
        const mappedSlabs = (data.slabs || []).map((slab, index) => {
          // Map discount type from API format to form format
          let discountType = '';
          const apiDiscountType = slab.box_discount_type || slab.piece_discount_type || '';

          if (apiDiscountType) {
            if (apiDiscountType.toLowerCase() === 'set_value' || apiDiscountType.toLowerCase() === 'set-value') {
              discountType = 'set-value';
            } else if (apiDiscountType.toLowerCase() === 'percentage' || apiDiscountType.toLowerCase() === 'percent') {
              discountType = 'percentage';
            } else if (apiDiscountType.toLowerCase() === 'fixed' || apiDiscountType.toLowerCase() === 'fixed_amount') {
              discountType = 'fixed';
            } else {
              discountType = apiDiscountType;
            }
          }

          // Map get products for this slab
          const mappedGetProducts = (slab.get_products || []).map((product, pIndex) => {
            const normalizedProduct = normalizeProductPricing(product);
            let resolvedType = product.product_type;
            if (!resolvedType || resolvedType === 'GET') {
              if (buyProductType === 'box-only') {
                resolvedType = 'Box';
              } else if (buyProductType === 'piece-only') {
                resolvedType = 'Piece';
              } else {
                resolvedType = 'Box';
              }
            }
            const isCompulsory =
              product.is_compulsory === '1' ||
              product.is_compulsory === 1 ||
              product.is_compulsory === true;
            const compulsoryQty = isCompulsory
              ? parseInt(product.compulsory_quantity, 10)
              : null;
            const fallbackQuantity =
              parseInt(product.quantity, 10) ||
              parseInt(product.compulsory_quantity, 10) ||
              1;
            const resolvedQuantity =
              isCompulsory && compulsoryQty !== null && !isNaN(compulsoryQty) && compulsoryQty > 0
                ? compulsoryQty
                : fallbackQuantity;

            const getLevelFallback = mappedGetPricingType;
            return {
              id: product.product_id,
              name: product.product_name,
              code: product.product_code,
              type: resolvedType,
              pricingType: normalizePieceBoxPricingSelect(
                product.box_pricing_type || product.piece_pricing_type || product.pricing_type,
                getLevelFallback
              ),
              boxPricingType: normalizePieceBoxPricingSelect(product.box_pricing_type, getLevelFallback),
              piecePricingType: normalizePieceBoxPricingSelect(product.piece_pricing_type, getLevelFallback),
              quantity: resolvedQuantity,
              stockDisplay: getProductStockValue(product),
              compulsory: isCompulsory,
              originalIndex: pIndex,
              costPrice: normalizedProduct.costPrice,
              salesPrice: normalizedProduct.boxSalesPrice,
              offerPrice: normalizedProduct.boxOfferPrice,
              boxCostPrice: normalizedProduct.boxCostPrice,
              boxSalesPrice: normalizedProduct.boxSalesPrice,
              boxOfferPrice: normalizedProduct.boxOfferPrice,
              pieceCostPrice: normalizedProduct.pieceCostPrice,
              pieceSalesPrice: normalizedProduct.pieceSalesPrice,
              pieceOfferPrice: normalizedProduct.pieceOfferPrice,
              split: parseInt(product.split) || 2,
              pack_size: parseInt(product.pack) || 1,
              stock: getProductStockValue(product),
              size: product.size || 'N/A',
            };
          });

          return {
            id: slab.slab_id || Date.now() + index,
            slabOrder: parseInt(slab.slab_order) || index + 1,
            slabName: slab.slab_name || `Slab ${index + 1}`,
            productType: buyProductType,
            boxMinQuantity: slab.box_min_quantity || '',
            boxMaxQuantity: slab.box_max_quantity || '',
            pieceMinQuantity: slab.piece_min_quantity || '',
            pieceMaxQuantity: slab.piece_max_quantity || '',
            discountType,
            boxProductsValue: slab.box_discount_value || '',
            pieceProductsValue: slab.piece_discount_value || '',
            hasGetProducts: slab.has_get_products === '1' || slab.has_get_products === 1,
            getQuantity: slab.get_quantity != null && slab.get_quantity !== '' ? parseInt(slab.get_quantity) : '',
            getProducts: mappedGetProducts,
          };
        });

        // Map discount configuration for Buy X Get Y at Discount
        const buyDiscountEnabled = data.buy_discount_enabled === true || data.buy_discount_enabled === '1';
        const getDiscountEnabled = data.get_discount_enabled === true || data.get_discount_enabled === '1';

        let buyDiscountType = 'percentage';
        let buyDiscountApplyTo = 'none';
        if (buyDiscountEnabled && rawBuyDiscountType) {
          const discountType = normalizeDiscountType(rawBuyDiscountType);
          if (discountType === 'percentage') buyDiscountType = 'percentage';
          else if (discountType === 'fixed') buyDiscountType = 'fixed';
          else if (discountType === 'set-value') buyDiscountType = 'set-value';
        }

        const discountMethod = (data.buy_discount_method || '').toUpperCase();
        if (discountMethod === 'ITEM_WISE') buyDiscountApplyTo = 'per-item';
        else if (discountMethod === 'BULK_ASSIGN') buyDiscountApplyTo = 'bulk';

        // Map get discount type
        let getDiscountType = 'set-value';
        let getItemDiscountApplyTo = 'bulk';
        if (getDiscountEnabled && data.get_discount_type) {
          const discountType = data.get_discount_type.toLowerCase();
          if (discountType === 'set_value' || discountType === 'set-value') {
            getDiscountType = 'set-value';
          } else {
            getDiscountType = 'value-off';
            const discountMethod = (data.get_discount_method || '').toUpperCase();
            if (discountMethod === 'ITEM_WISE') getItemDiscountApplyTo = 'per-item';
            else if (discountMethod === 'BULK_ASSIGN') getItemDiscountApplyTo = 'bulk';
            else getItemDiscountApplyTo = 'bulk';
          }
        }

        const discountMode = (data.buy_discount_mode || data.buy_discount_config_type || '').toUpperCase();
        const normalizedBuyDiscountType = normalizeDiscountType(rawBuyDiscountType);
        const resolvedBuyDiscountConfigType = discountMode === 'SET_VALUE' ? 'set-value' : 'value-off';
        const resolvedBuyXDiscountType = (() => {
          if (resolvedBuyDiscountConfigType !== 'value-off') return 'no-discount';
          if (normalizedBuyDiscountType === 'percentage') return 'percentage';
          if (normalizedBuyDiscountType === 'fixed') return 'fixed';
          return 'percentage';
        })();
        const resolvedBuyXDiscountApplyTo = resolvedBuyDiscountConfigType === 'value-off'
          ? (data.buy_discount_method === 'ITEM_WISE' ? 'per-item' : 'bulk')
          : 'bulk';


        const buildGroupQuantitiesFromProducts = (products) => {
          const map = {};
          (products || []).forEach((product) => {
            const g =
              product.groupNo != null && String(product.groupNo).trim() !== ""
                ? String(product.groupNo).trim()
                : "";
            if (!g) return;
            // Prefer API group_quantity; 0 / missing stays empty (optional).
            const raw = product.group_quantity ?? product.groupQuantity ?? product.quantity;
            const q = parseInt(raw, 10);
            if (!Number.isFinite(q) || q <= 0) {
              if (map[g] === undefined) map[g] = '';
              return;
            }
            map[g] = String(Math.max(parseInt(map[g] || '0', 10) || 0, q));
          });
          return map;
        };
        const buyGroupQuantities = buildGroupQuantitiesFromProducts(mappedBuyProducts);
        const getGroupQuantities = buildGroupQuantitiesFromProducts(mappedGetProducts);

        // Map API data to form data
        setFormData({
          title: data.title || '',
          description: data.description || '',
          promotionType: mappedPromotionType,
          startDate: data.start_date_raw ? moment(data.start_date_raw) : null,
          endDate: data.end_date_raw ? moment(data.end_date_raw) : null,
          selectedChannels: parsedChannels,
          buyProducts: mappedBuyProducts,
          getProducts: mappedGetProducts,
          buyProductType,
          slabs: mappedSlabs,
          // Add promotion-level quantities and pricing types
          buyQuantity: parseInt(data.buy_quantity) || 1,
          getQuantity: parseInt(data.get_quantity) || 1,
          buyGroupQuantities,
          getGroupQuantities,
          buyPricingType: finalBuyPricingType,
          getPricingType: mappedGetPricingType,
          // Map Buy X at Discount configuration
          buyXDiscountType: resolvedBuyXDiscountType,
          buyXDiscountValue: resolvedBuyDiscountConfigType === 'value-off' ? (data.buy_discount_value || '') : '',
          buyXDiscountApplyTo: resolvedBuyXDiscountApplyTo,
          buyDiscountConfigType: resolvedBuyDiscountConfigType,
          buySetValue: resolvedBuyDiscountConfigType === 'set-value' ? (data.buy_discount_value || '') : '',
          // Map Buy X Get Y at Discount configuration
          buyDiscountType,
          buyDiscountValue: data.buy_discount_value || '',
          buyDiscountApplyTo,
          getDiscountType,
          getSetValue: getDiscountType === 'set-value' ? (data.get_discount_value || '') : '',
          getItemDiscountType: getDiscountType === 'value-off' ? (data.get_discount_type === 'PERCENTAGE' ? 'percentage' : 'fixed') : 'percentage',
          getItemDiscountValue: getDiscountType === 'value-off' ? (data.get_discount_value || '') : '',
          getItemDiscountApplyTo,
        });
      } else {
        setError(response.message || 'Failed to load promotion data');
      }
    } catch (error) {
      setError('Failed to load promotion data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleChannelToggle = (channelValue) => {
    const channel = channels.find((ch) => ch.value === channelValue);
    const isMobileApp = channel?.isMobileApp || false;

    setFormData((prev) => {
      if (isMobileApp) {
        const hasMobileApp = mobileAppChannelValue && prev.selectedChannels.includes(mobileAppChannelValue);
        if (hasMobileApp) {
          const newChannels = prev.selectedChannels.filter((c) => c !== mobileAppChannelValue);
          if (websiteChannelValue && newChannels.includes(websiteChannelValue)) {
            return {
              ...prev,
              selectedChannels: newChannels.filter((c) => c !== websiteChannelValue),
            };
          }
          return {
            ...prev,
            selectedChannels: newChannels,
          };
        } else {
          const newChannels = [...prev.selectedChannels];
          if (mobileAppChannelValue && !newChannels.includes(mobileAppChannelValue)) {
            newChannels.push(mobileAppChannelValue);
          }
          if (websiteChannelValue && !newChannels.includes(websiteChannelValue)) {
            newChannels.push(websiteChannelValue);
          }
          return {
            ...prev,
            selectedChannels: newChannels,
          };
        }
      } else {
        return {
          ...prev,
          selectedChannels: prev.selectedChannels.includes(channelValue)
            ? prev.selectedChannels.filter((c) => c !== channelValue)
            : [...prev.selectedChannels, channelValue],
        };
      }
    });
  };

  const handleProductTypeChange = (productId, type, newType) => {
    setFormData((prev) => ({
      ...prev,
      [type === 'buy' ? 'buyProducts' : 'getProducts']: prev[
        type === 'buy' ? 'buyProducts' : 'getProducts'
      ].map((product, index) => {
        if (`${product.id}-${index}` === productId) {
          return { ...product, type: newType || getDefaultProductType(product.split) };
        }
        const currentType = product.type && product.type !== '' && product.type !== 'undefined' && product.type !== 'null'
          ? product.type
          : getDefaultProductType(product.split);
        return { ...product, type: currentType };
      }),
    }));
  };

  const handleProductPricingChange = (productId, type, pricingType) => {
    setFormData((prev) => ({
      ...prev,
      [type === 'buy' ? 'buyProducts' : 'getProducts']: prev[
        type === 'buy' ? 'buyProducts' : 'getProducts'
      ].map((product, index) =>
        `${product.id}-${index}` === productId ? { ...product, pricingType } : product
      ),
    }));
  };

  const handleRemoveProduct = (productId, type) => {
    setFormData((prev) => ({
      ...prev,
      [type === 'buy' ? 'buyProducts' : 'getProducts']: prev[
        type === 'buy' ? 'buyProducts' : 'getProducts'
      ].filter((p, index) => `${p.id}-${index}` !== productId),
    }));
  };

  const handleProductQuantityChange = (productId, type, quantity) => {
    setFormData((prev) => ({
      ...prev,
      [type === 'buy' ? 'buyProducts' : 'getProducts']: prev[
        type === 'buy' ? 'buyProducts' : 'getProducts'
      ].map((product, index) =>
        `${product.id}-${index}` === productId
          ? { ...product, quantity: quantity === '' ? '' : (parseInt(quantity) || 1) }
          : product
      ),
    }));
  };

  const handleProductCompulsoryChange = (productId, type, compulsory) => {
    setFormData((prev) => ({
      ...prev,
      [type === 'buy' ? 'buyProducts' : 'getProducts']: prev[
        type === 'buy' ? 'buyProducts' : 'getProducts'
      ].map((product, index) =>
        `${product.id}-${index}` === productId
          ? {
            ...product,
            compulsory,
            quantity: compulsory && (!product.quantity || product.quantity === '') ? 1 : product.quantity
          }
          : product
      ),
    }));
  };

  const handleProductItemDiscountTypeChange = (productId, discountType) => {
    setFormData((prev) => ({
      ...prev,
      getProducts: prev.getProducts.map((product, index) =>
        `${product.id}-${index}` === productId
          ? { ...product, itemDiscountType: discountType }
          : product
      ),
    }));
  };

  const handleProductItemDiscountValueChange = (productId, discountValue) => {
    setFormData((prev) => ({
      ...prev,
      getProducts: prev.getProducts.map((product, index) =>
        `${product.id}-${index}` === productId
          ? { ...product, itemDiscountValue: discountValue }
          : product
      ),
    }));
  };

  const handleSlabProductTypeChange = (slabIndex, productId, newType) => {
    const newSlabs = [...(formData.slabs || [])];
    newSlabs[slabIndex] = {
      ...newSlabs[slabIndex],
      getProducts: (newSlabs[slabIndex].getProducts || []).map((product, index) =>
        `${product.id}-${index}` === productId ? { ...product, type: newType } : product
      ),
    };
    setFormData((prev) => ({ ...prev, slabs: newSlabs }));
  };

  const handleSlabProductPricingChange = (slabIndex, productId, field, value) => {
    const newSlabs = [...(formData.slabs || [])];
    newSlabs[slabIndex] = {
      ...newSlabs[slabIndex],
      getProducts: (newSlabs[slabIndex].getProducts || []).map((product, index) => {
        if (`${product.id}-${index}` !== productId) return product;
        const pt =
          product.type && product.type !== '' && product.type !== 'undefined' && product.type !== 'null'
            ? product.type
            : getDefaultProductType(product.split);
        const next = { ...product, [field]: value };
        if (field !== 'pricingType') return next;
        if (pt === 'Piece') return { ...next, piecePricingType: value };
        if (pt === 'Box') return { ...next, boxPricingType: value };
        if (pt === 'Both') return { ...next, boxPricingType: value, piecePricingType: value };
        return next;
      }),
    };
    setFormData((prev) => ({ ...prev, slabs: newSlabs }));
  };

  const handleSlabProductQuantityChange = (slabIndex, productId, quantity) => {
    const newSlabs = [...(formData.slabs || [])];
    newSlabs[slabIndex] = {
      ...newSlabs[slabIndex],
      getProducts: (newSlabs[slabIndex].getProducts || []).map((product, index) =>
        `${product.id}-${index}` === productId
          ? { ...product, quantity: quantity === '' ? '' : (parseInt(quantity) || 1) }
          : product
      ),
    };
    setFormData((prev) => ({ ...prev, slabs: newSlabs }));
  };

  const handleSlabProductCompulsoryChange = (slabIndex, productId, checked) => {
    const newSlabs = [...(formData.slabs || [])];
    newSlabs[slabIndex] = {
      ...newSlabs[slabIndex],
      getProducts: (newSlabs[slabIndex].getProducts || []).map((product, index) =>
        `${product.id}-${index}` === productId
          ? {
            ...product,
            compulsory: checked,
            quantity: checked && (!product.quantity || product.quantity === '') ? 1 : product.quantity
          }
          : product
      ),
    };
    setFormData((prev) => ({ ...prev, slabs: newSlabs }));
  };

  const handleSlabRemoveProduct = (slabIndex, productId) => {
    const newSlabs = [...(formData.slabs || [])];
    newSlabs[slabIndex] = {
      ...newSlabs[slabIndex],
      getProducts: (newSlabs[slabIndex].getProducts || []).filter(
        (p, index) => `${p.id}-${index}` !== productId
      ),
    };
    setFormData((prev) => ({ ...prev, slabs: newSlabs }));
  };

  const handleOpenProductModal = (type) => {
    setModalType(type);
    setModalTitle(type === 'buy' ? 'Select Buy Products' : 'Select Get Products');
    setShowProductModal(true);
  };

  const handleCloseProductModal = () => {
    setShowProductModal(false);
  };

  const getDefaultTypeForMultiLayer = (product) => {
    if (formData.promotionType === 'multi-layer-offer') {
      const buyProductType = formData.buyProductType || 'box-only';

      // For get products (getForSlab)
      if (modalType === 'getForSlab') {
        if (buyProductType === 'box-only') return 'Box';
        if (buyProductType === 'piece-only') return 'Piece';
        if (buyProductType === 'both') return 'Box'; // Always default to Box for 'both' in get products
      }

      // For buy products
      if (modalType === 'buy') {
        if (buyProductType === 'box-only') return 'Box';
        if (buyProductType === 'piece-only') return 'Piece';
        if (buyProductType === 'both') {
          if (product.split === 0) return 'Box';
          if (product.split === 1) return 'Piece';
          if (product.split === 2) return 'Both';
        }
      }
    }
    const hasValidType = product.type && product.type !== '' && product.type !== 'undefined' && product.type !== 'null';
    return hasValidType ? product.type : getDefaultProductType(product.split);
  };

  const handleConfirmProducts = (selectedProducts) => {

    const productsWithType = selectedProducts.map((product, index) => {
      const productId = product.product_id || product.id;
      const productCode = product.product_code || product.code;

      const productData = {
        ...product,
        id: productId,
        code: productCode || productId,
        type: getDefaultTypeForMultiLayer(product),
        pricingType: product.pricingType || 'sales',
        boxPricingType: product.boxPricingType || 'sales',
        piecePricingType: product.piecePricingType || 'sales',
        compulsory: product.compulsory || false,
        quantity: product.quantity || 1,
        originalIndex: index,
        buyXItemDiscountType: product.buyXItemDiscountType || 'no-discount',
        buyXItemDiscountValue: product.buyXItemDiscountValue || '',
      };
      return productData;
    });

    if (modalType === 'getForSlab' && currentSlabIndex !== null) {
      const newSlabs = [...(formData.slabs || [])];
      newSlabs[currentSlabIndex] = {
        ...newSlabs[currentSlabIndex],
        getProducts: productsWithType,
      };
      setFormData((prev) => ({
        ...prev,
        slabs: newSlabs,
      }));
      setCurrentSlabIndex(null);
    } else {
      setFormData((prev) => ({
        ...prev,
        [modalType === 'buy' ? 'buyProducts' : 'getProducts']: productsWithType,
      }));
    }
    setShowProductModal(false);
  };

  const getDefaultProductType = (split) => {
    if (split === 0) return 'Box';
    if (split === 1) return 'Piece';
    return 'Box';
  };

  const validateBuyXGetYFree = () => {
    const errors = {};

    if (formData.buyProducts.length === 0) {
      errors.buyProducts = 'Please add at least one buy product';
    } else if (formData.buyProducts.length > 200) {
      errors.buyProducts = `You cannot add more than 200 products. Current count: ${formData.buyProducts.length}`;
    }

    formData.buyProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`buyProduct_${index}_type`] = 'Product Type is necessary';
      }
      if (!product.pricingType) {
        errors[`buyProduct_${index}_pricing`] = 'Pricing Type is necessary';
      }
      if (product.compulsory && (!product.quantity || product.quantity === '' || parseInt(product.quantity) < 1)) {
        errors[`buyProduct_${index}_quantity`] = 'Quantity is required for compulsory products';
      }
    });

    if (!formData.buyQuantity || formData.buyQuantity === '') {
      errors.buyQuantity = 'Buy quantity is required and must be a positive number';
    } else {
      const buyQty = parseInt(formData.buyQuantity);
      if (isNaN(buyQty) || buyQty < 1) {
        errors.buyQuantity = 'Buy quantity must be at least 1';
      } else if (buyQty > 9999) {
        errors.buyQuantity = 'Buy quantity cannot exceed 9999';
      } else {
        const compulsoryCount = formData.buyProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (p.quantity || 1), 0);
        if (compulsoryCount > buyQty) {
          errors.buyQuantity = `Compulsory quantity (${compulsoryCount}) cannot exceed buy quantity (${buyQty})`;
        }
      }
    }

    if (formData.getProducts.length === 0) {
      errors.getProducts = 'Please add at least one get product';
    } else if (formData.getProducts.length > 200) {
      errors.getProducts = 'You cannot add more than 200 products to get list';
    }

    formData.getProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`getProduct_${index}_type`] = 'Product Type is necessary';
      }
      if (!product.pricingType) {
        errors[`getProduct_${index}_pricing`] = 'Pricing Type is necessary';
      }
      if (product.compulsory && (!product.quantity || product.quantity === '' || parseInt(product.quantity) < 1)) {
        errors[`getProduct_${index}_quantity`] = 'Quantity is required for compulsory products';
      }
    });

    if (!formData.getQuantity || formData.getQuantity === '') {
      errors.getQuantity = 'Get quantity is required and must be a positive number';
    } else {
      const getQty = parseInt(formData.getQuantity);
      if (isNaN(getQty) || getQty < 1) {
        errors.getQuantity = 'Get quantity must be at least 1';
      } else if (getQty > 9999) {
        errors.getQuantity = 'Get quantity cannot exceed 9999';
      } else {
        const compulsoryCount = formData.getProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (p.quantity || 1), 0);
        if (compulsoryCount > getQty) {
          errors.getQuantity = `Compulsory quantity (${compulsoryCount}) cannot exceed get quantity (${getQty})`;
        }
      }
    }

    return errors;
  };

  const getNormalizedProductType = (product) => {
    if (product.type && product.type !== '' && product.type !== 'undefined' && product.type !== 'null') {
      return product.type;
    }
    if (product.split === 1) return 'Piece';
    return 'Box';
  };

  const getProductUnitPrice = (product) => {
    const type = getNormalizedProductType(product);
    const pricingMode =
      product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null'
        ? product.pricingType
        : 'sales';

    const pickPrice = (...values) => {
      for (const value of values) {
        const parsed = parseFloat(value);
        if (!Number.isNaN(parsed)) {
          return parsed;
        }
      }
      return 0;
    };

    if (type === 'Piece') {
      if (pricingMode === 'offer') {
        return pickPrice(product.pieceOfferPrice, product.offerPiecePrice, product.offerPrice, product.price);
      }
      return pickPrice(product.pieceSalesPrice, product.piece_sales_price, product.PiecePrice, product.salesPrice, product.price);
    }

    if (pricingMode === 'offer') {
      return pickPrice(product.boxOfferPrice, product.offerBoxPrice, product.offerPrice);
    }
    return pickPrice(product.boxSalesPrice, product.salesPrice, product.price);
  };

  const getProductTotalValue = (product) => {
    const quantity = parseInt(product.quantity, 10);
    const normalizedQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    return getProductUnitPrice(product) * normalizedQuantity;
  };

  const getProductsTotalValue = (products = []) =>
    products.reduce((sum, product) => sum + getProductTotalValue(product), 0);

  const getProductsBulkTotalValue = (products = []) =>
    products.reduce((sum, product) => sum + getProductUnitPrice(product), 0);

  const validateBuyXGetYDiscount = () => {
    const errors = {};

    if (formData.buyProducts.length === 0) {
      errors.buyProducts = 'Please add at least one buy product';
    } else if (formData.buyProducts.length > 200) {
      errors.buyProducts = `You cannot add more than 200 products. Current count: ${formData.buyProducts.length}`;
    }

    formData.buyProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`buyProduct_${index}_type`] = 'Product Type is necessary';
      }
      if (!product.pricingType) {
        errors[`buyProduct_${index}_pricing`] = 'Please select price type (Sales Price or Offer Price)';
      }
    });

    if (!formData.buyQuantity || formData.buyQuantity === '') {
      errors.buyQuantity = 'Buy quantity is required and must be a positive number';
    } else {
      const buyQty = parseInt(formData.buyQuantity);
      if (isNaN(buyQty) || buyQty < 1) {
        errors.buyQuantity = 'Buy quantity must be at least 1';
      } else if (buyQty > 9999) {
        errors.buyQuantity = 'Buy quantity cannot exceed 9999';
      } else {
        const compulsoryCount = formData.buyProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (p.quantity || 1), 0);
        if (compulsoryCount > buyQty) {
          errors.buyQuantity = `Compulsory quantity (${compulsoryCount}) cannot exceed buy quantity (${buyQty})`;
        }
      }
    }

    if (formData.getProducts.length === 0) {
      errors.getProducts = 'Please add at least one get product';
    } else if (formData.getProducts.length > 200) {
      errors.getProducts = 'You cannot add more than 200 products to get list';
    }

    formData.getProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`getProduct_${index}_type`] = 'Product Type is necessary';
      }
      if (!product.pricingType) {
        errors[`getProduct_${index}_pricing`] = 'Please select price type (Sales Price or Offer Price)';
      }
    });

    if (!formData.getQuantity || formData.getQuantity === '') {
      errors.getQuantity = 'Get quantity is required and must be a positive number';
    } else {
      const getQty = parseInt(formData.getQuantity);
      if (isNaN(getQty) || getQty < 1) {
        errors.getQuantity = 'Get quantity must be at least 1';
      } else if (getQty > 9999) {
        errors.getQuantity = 'Get quantity cannot exceed 9999';
      } else {
        const compulsoryCount = formData.getProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (p.quantity || 1), 0);
        if (compulsoryCount > getQty) {
          errors.getQuantity = `Compulsory quantity (${compulsoryCount}) cannot exceed get quantity (${getQty})`;
        }
      }
    }

    if (formData.buyDiscountApplyTo && formData.buyDiscountApplyTo !== 'none') {
      if (formData.buyDiscountApplyTo === 'bulk') {
        if (!formData.buyDiscountValue || formData.buyDiscountValue === '') {
          errors.buyDiscountValue = 'Please enter a discount value';
        } else {
          const discountValue = parseFloat(formData.buyDiscountValue);
          if (isNaN(discountValue)) {
            errors.buyDiscountValue = 'Discount value must be a valid number';
          } else if (formData.buyDiscountType === 'percentage') {
            if (discountValue < 0 || discountValue > 100) {
              errors.buyDiscountValue = 'Discount percentage must be between 0 and 100';
            }
          } else if (formData.buyDiscountType === 'fixed') {
            if (discountValue < 0) {
              errors.buyDiscountValue = 'Fixed discount cannot be negative';
            } else {
              const productsWithInvalidDiscount = formData.buyProducts.filter((product) => {
                const productPrice = getProductUnitPrice(product);
                return productPrice > 0 && discountValue > productPrice;
              });
              if (productsWithInvalidDiscount.length > 0) {
                const productDetails = productsWithInvalidDiscount.map(p => {
                  const price = getProductUnitPrice(p);
                  return `${p.name || p.product_name} (£${price.toFixed(2)})`;
                }).join(', ');
                errors.buyDiscountValue = `Discount amount (£${discountValue.toFixed(2)}) must be less than or equal to the product price. Invalid products: ${productDetails}`;
              } else {
                const productsThatWouldBeFree = formData.buyProducts.filter((product) => {
                  const productPrice = getProductUnitPrice(product);
                  return productPrice > 0 && discountValue === productPrice;
                });
                if (productsThatWouldBeFree.length > 0) {
                  const productNames = productsThatWouldBeFree.map(p => p.name || p.product_name).join(', ');
                  errors.buyDiscountValue = `Warning: Fixed discount (£${discountValue.toFixed(2)}) is equal to the price of some products (${productNames}), which may result in those products being free.`;
                }
              }
            }
          }
        }
      } else if (formData.buyDiscountApplyTo === 'per-item') {
        formData.buyProducts.forEach((product, index) => {
          if (!product.buyItemDiscountValue || product.buyItemDiscountValue === '') {
            errors[`buyProduct_${index}_discountValue`] = 'Discount value is required for per-item discount';
          } else {
            const discountValue = parseFloat(product.buyItemDiscountValue);
            if (isNaN(discountValue)) {
              errors[`buyProduct_${index}_discountValue`] = 'Discount value must be a valid number';
            } else if (product.buyItemDiscountType === 'percentage') {
              if (discountValue < 0 || discountValue > 100) {
                errors[`buyProduct_${index}_discountValue`] =
                  'Discount percentage must be between 0 and 100';
              }
            } else if (product.buyItemDiscountType === 'fixed') {
              const productPrice = getProductUnitPrice(product);
              if (discountValue < 0) {
                errors[`buyProduct_${index}_discountValue`] = 'Fixed discount cannot be negative';
              } else if (productPrice > 0 && discountValue >= productPrice) {
                errors[`buyProduct_${index}_discountValue`] =
                  `Fixed discount (£${discountValue.toFixed(2)}) cannot exceed product price (£${productPrice.toFixed(2)})`;
              }
            }
          }
        });
      }
    }

    if (formData.getDiscountType === 'set-value') {
      if (!formData.getSetValue || formData.getSetValue === '') {
        errors.getSetValue = 'Please enter a set value';
      } else {
        const setValue = parseFloat(formData.getSetValue);
        if (isNaN(setValue)) {
          errors.getSetValue = 'Set value must be a valid number';
        } else if (setValue <= 0) {
          errors.getSetValue = 'Set value must be greater than 0';
        } else {
          let getProductsTotal = 0;
          formData.getProducts.forEach((product) => {
            const qty = product.quantity || 1;
            const price =
              product.pricingType === 'offer'
                ? product.offerPrice || 0
                : product.salesPrice || product.price || 0;
            getProductsTotal += price * qty;
          });

          if (setValue > getProductsTotal && getProductsTotal > 0) {
            errors.getSetValueWarning = `Set value (£${setValue.toFixed(2)}) is higher than product total (£${getProductsTotal.toFixed(2)}). System will use £${getProductsTotal.toFixed(2)}`;
          }

          if (setValue === 0) {
            errors.getSetValueWarning = 'Get products will be free. Confirm this is correct?';
          }
        }
      }
    } else if (formData.getDiscountType === 'value-off') {
      if (formData.getItemDiscountApplyTo === 'bulk') {
        if (!formData.getItemDiscountValue || formData.getItemDiscountValue === '') {
          errors.getItemDiscountValue = 'Please enter a discount value';
        } else {
          const discountValue = parseFloat(formData.getItemDiscountValue);
          if (isNaN(discountValue)) {
            errors.getItemDiscountValue = 'Discount value must be a valid number';
          } else if (formData.getItemDiscountType === 'percentage') {
            if (discountValue < 0 || discountValue > 100) {
              errors.getItemDiscountValue = 'Discount percentage must be between 0 and 100';
            }
          } else if (formData.getItemDiscountType === 'fixed') {
            if (discountValue < 0) {
              errors.getItemDiscountValue = 'Fixed discount cannot be negative';
            } else {
              const productsWithInvalidDiscount = formData.getProducts.filter((product) => {
                const productPrice = getProductUnitPrice(product);
                return productPrice > 0 && discountValue > productPrice;
              });
              if (productsWithInvalidDiscount.length > 0) {
                const productDetails = productsWithInvalidDiscount.map(p => {
                  const price = getProductUnitPrice(p);
                  return `${p.name || p.product_name} (£${price.toFixed(2)})`;
                }).join(', ');
                errors.getItemDiscountValue = `Discount amount (£${discountValue.toFixed(2)}) must be less than or equal to the product price. Invalid products: ${productDetails}`;
              } else {
                const productsThatWouldBeFree = formData.getProducts.filter((product) => {
                  const productPrice = getProductUnitPrice(product);
                  return productPrice > 0 && discountValue === productPrice;
                });
                if (productsThatWouldBeFree.length > 0) {
                  const productNames = productsThatWouldBeFree.map(p => p.name || p.product_name).join(', ');
                  errors.getItemDiscountValue = `Warning: Fixed discount (£${discountValue.toFixed(2)}) is equal to the price of some products (${productNames}), which may result in those products being free.`;
                }
              }
            }
          }
        }
      } else if (formData.getItemDiscountApplyTo === 'per-item') {
        formData.getProducts.forEach((product, index) => {
          if (!product.itemDiscountValue || product.itemDiscountValue === '') {
            errors[`getProduct_${index}_discountValue`] = 'Discount value is required for per-item discount';
          } else {
            const discountValue = parseFloat(product.itemDiscountValue);
            if (isNaN(discountValue)) {
              errors[`getProduct_${index}_discountValue`] = 'Discount value must be a valid number';
            } else if (product.itemDiscountType === 'percentage') {
              if (discountValue < 0 || discountValue > 100) {
                errors[`getProduct_${index}_discountValue`] =
                  'Discount percentage must be between 0 and 100';
              }
            } else if (product.itemDiscountType === 'fixed') {
              const productPrice = getProductUnitPrice(product);
              if (discountValue < 0) {
                errors[`getProduct_${index}_discountValue`] = 'Fixed discount cannot be negative';
              } else if (productPrice > 0 && discountValue >= productPrice) {
                errors[`getProduct_${index}_discountValue`] =
                  `Fixed discount (£${discountValue.toFixed(2)}) cannot exceed product price (£${productPrice.toFixed(2)})`;
              }
            }
          }
        });
      }
    }

    return errors;
  };

  const validateGroupOffer = () => {
    const errors = validateBuyXGetYDiscount();
    const allowed = new Set(Array.from({ length: 10 }, (_, i) => String(i + 1)));
    formData.buyProducts.forEach((product, index) => {
      const g = product.groupNo != null && String(product.groupNo).trim() !== '' ? String(product.groupNo) : '';
      if (!allowed.has(g)) {
        errors[`buyProduct_${index}_group`] = 'Group is required. Choose group 1 to 10';
      }
    });

    formData.getProducts.forEach((product, index) => {
      const g = product.groupNo != null && String(product.groupNo).trim() !== '' ? String(product.groupNo) : '';
      if (!allowed.has(g)) {
        errors[`getProduct_${index}_group`] = 'Group is required. Choose group 1 to 10';
      }
    });

    // Group qty is optional. When filled it must stay within the side total,
    // e.g. Buy 2 cannot be split as Group 1 = 2 plus Group 2 = 1 (total 3).
    const validateGroupQtyWithinTotal = (groupKeys, quantities, totalRaw, errorKey, sideLabel) => {
      let sum = 0;
      let filledCount = 0;

      groupKeys.forEach((g) => {
        const raw = quantities?.[g];
        if (raw === undefined || raw === null || String(raw).trim() === '') return;
        const qty = parseInt(raw, 10);
        if (!Number.isFinite(qty) || qty < 1) {
          errors[`${errorKey}_${g}`] = 'Qty must be at least 1';
          return;
        }
        filledCount += 1;
        sum += qty;
      });

      const total = parseInt(totalRaw, 10);
      if (filledCount > 0 && Number.isFinite(total) && total > 0 && sum > total) {
        errors[errorKey] =
          `${sideLabel} group qty total (${sum}) cannot be greater than Total ${sideLabel} Quantity (${total}).`;
      }
    };

    // Collect distinct group ids used by buy/get products (no per-group product cap).
    const buyGroups = [
      ...new Set(
        formData.buyProducts
          .map((product) => String(product.groupNo))
          .filter((g) => allowed.has(g))
      ),
    ].sort((a, b) => Number(a) - Number(b));
    validateGroupQtyWithinTotal(
      buyGroups,
      formData.buyGroupQuantities,
      formData.buyQuantity,
      'buyGroupQty',
      'Buy'
    );

    const getGroups = [
      ...new Set(
        formData.getProducts
          .map((product) => String(product.groupNo))
          .filter((g) => allowed.has(g))
      ),
    ].sort((a, b) => Number(a) - Number(b));
    validateGroupQtyWithinTotal(
      getGroups,
      formData.getGroupQuantities,
      formData.getQuantity,
      'getGroupQty',
      'Get'
    );

    return errors;
  };

  const validateBuyXDiscount = () => {
    const errors = {};

    if (formData.buyProducts.length === 0) {
      errors.buyProducts = 'Please add at least one product to the promotion';
    } else if (formData.buyProducts.length > 200) {
      errors.buyProducts = `Maximum 200 products allowed. Current: ${formData.buyProducts.length} products`;
    }

    formData.buyProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`buyProduct_${index}_type`] = 'Please select product type (Box or Piece)';
      }
      if (!product.pricingType) {
        errors[`buyProduct_${index}_pricing`] = 'Please select price type (Sales Price or Offer Price)';
      }
      if (product.pricingType === 'offer') {
        if (!product.offerPrice || product.offerPrice === '' || parseFloat(product.offerPrice) === 0) {
          errors[`buyProduct_${index}_offerPrice`] = 'Warning: Offer price is set to 0. Please verify or use a valid price.';
        } else if (parseFloat(product.offerPrice) < 0) {
          errors[`buyProduct_${index}_offerPrice`] = 'Offer price cannot be negative';
        }
      }
      if (product.compulsory && (!product.quantity || product.quantity === '' || parseInt(product.quantity) < 1)) {
        errors[`buyProduct_${index}_quantity`] = 'Quantity is required and must be at least 1 for compulsory products';
      }
    });

    if (!formData.buyQuantity || formData.buyQuantity === '') {
      errors.buyQuantity = 'Buy quantity is required and must be a positive whole number';
    } else {
      const buyQty = parseInt(formData.buyQuantity);
      if (isNaN(buyQty) || buyQty < 1) {
        errors.buyQuantity = 'Buy quantity is required and must be a positive whole number';
      } else if (buyQty > 9999) {
        errors.buyQuantity = 'Buy quantity must be between 1 and 9999';
      } else {
        const compulsoryCount = formData.buyProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (parseInt(p.quantity) || 1), 0);
        if (compulsoryCount > 0 && compulsoryCount > buyQty) {
          errors.compulsoryQuantity = `Total compulsory quantity (${compulsoryCount}) must be less than or equal to buy quantity (${buyQty})`;
        }
      }
    }

    if (formData.buyDiscountConfigType === 'set-value') {
      if (!formData.buySetValue || formData.buySetValue === '') {
        errors.buySetValue = 'Please enter a set value for the promotion';
      } else {
        const setValue = parseFloat(formData.buySetValue);
        if (isNaN(setValue)) {
          errors.buySetValue = 'Please enter a valid numeric value';
        } else if (setValue <= 0) {
          errors.buySetValue = 'Set value must be greater than 0';
        } else {
          let productsTotal = 0;
          formData.buyProducts.forEach((product) => {
            const qty = product.quantity || 1;
            const price =
              product.pricingType === 'offer'
                ? product.offerPrice || 0
                : product.salesPrice || product.price || 0;
            productsTotal += price * qty;
          });

          if (setValue > productsTotal && productsTotal > 0) {
            errors.buySetValueWarning = `Set value (£${setValue.toFixed(2)}) is higher than product total (£${productsTotal.toFixed(2)}). System will use £${productsTotal.toFixed(2)}.`;
          }
        }
      }
    } else if (formData.buyDiscountConfigType === 'value-off') {
      if (formData.buyXDiscountApplyTo === 'bulk') {
        if (!formData.buyXDiscountType || formData.buyXDiscountType === 'no-discount') {
          errors.buyXDiscountType = 'Please select discount type (Percentage or Fixed Amount)';
        } else {
          if (!formData.buyXDiscountValue || formData.buyXDiscountValue === '') {
            errors.buyXDiscountValue = 'Please enter a discount value';
          } else {
            const discountValue = parseFloat(formData.buyXDiscountValue);
            if (isNaN(discountValue)) {
              errors.buyXDiscountValue = 'Please enter a valid numeric value';
            } else if (formData.buyXDiscountType === 'percentage') {
              if (discountValue < 0 || discountValue > 100) {
                errors.buyXDiscountValue = 'Percentage must be between 0 and 100';
              }
            } else if (formData.buyXDiscountType === 'fixed') {
              if (discountValue < 0) {
                errors.buyXDiscountValue = 'Discount amount cannot be negative';
              } else {
                const productsWithInvalidDiscount = formData.buyProducts.filter((product) => {
                  const productPrice = getProductUnitPrice(product);
                  return productPrice > 0 && discountValue > productPrice;
                });
                if (productsWithInvalidDiscount.length > 0) {
                  const productDetails = productsWithInvalidDiscount.map(p => {
                    const price = getProductUnitPrice(p);
                    return `${p.name || p.product_name} (£${price.toFixed(2)})`;
                  }).join(', ');
                  errors.buyXDiscountValue = `Discount amount (£${discountValue.toFixed(2)}) must be less than or equal to the product price. Invalid products: ${productDetails}`;
                }
              }
            }
          }
        }
      } else if (formData.buyXDiscountApplyTo === 'per-item') {
        formData.buyProducts.forEach((product, index) => {
          if (product.buyXItemDiscountType && product.buyXItemDiscountType !== 'no-discount') {
            if (!product.buyXItemDiscountValue || product.buyXItemDiscountValue === '') {
              errors[`buyProduct_${index}_discountValue`] = 'Please enter a discount value';
            } else {
              const discountValue = parseFloat(product.buyXItemDiscountValue);
              const productPrice = getProductUnitPrice(product);

              if (isNaN(discountValue)) {
                errors[`buyProduct_${index}_discountValue`] = 'Please enter a valid numeric value';
              } else if (product.buyXItemDiscountType === 'percentage') {
                if (discountValue < 0 || discountValue > 100) {
                  errors[`buyProduct_${index}_discountValue`] = 'Percentage must be between 0 and 100';
                }
              } else if (product.buyXItemDiscountType === 'fixed') {
                if (discountValue < 0) {
                  errors[`buyProduct_${index}_discountValue`] = 'Discount amount cannot be negative';
                } else if (productPrice > 0 && discountValue > productPrice) {
                  const productLabel =
                    product.name || product.product_name || product.code || `Product ${index + 1}`;
                  errors[`buyProduct_${index}_discountValue`] =
                    `Discount amount (£${discountValue.toFixed(2)}) must be less than or equal to the product price (£${productPrice.toFixed(2)}). Invalid product: ${productLabel}`;
                }
              }
            }
          }
        });
      }
    }

    return errors;
  };

  useEffect(() => {
    if (formData.description && formData.description.length > 500) {
      setErrors((prev) => ({
        ...prev,
        description: 'Description cannot exceed 500 characters',
      }));
    } else if (errors.description === 'Description cannot exceed 500 characters') {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (formData.description.trim()) {
          delete newErrors.description;
        } else {
          newErrors.description = 'Description is required';
        }
        return newErrors;
      });
    }
  }, [formData.description]);

  const validateMultiLayerOffer = () => {
    const errors = {};

    if (formData.buyProducts.length === 0) {
      errors.buyProducts = 'Please add at least one product for the multi-layer offer';
    } else if (formData.buyProducts.length > 200) {
      errors.buyProducts = 'Cannot exceed 200 products in a multi-layer offer';
    }

    const buyProductType = formData.buyProductType || 'box-only';
    formData.buyProducts.forEach((product, index) => {
      const split = product.split;
      let isValidForProductType = true;

      if (buyProductType === 'box-only' && split !== 0 && split !== 2) {
        isValidForProductType = false;
        errors[`buyProduct_${index}_type`] = 'This product does not support Box type. Please remove and select compatible products.';
      } else if (buyProductType === 'piece-only' && split !== 1 && split !== 2) {
        isValidForProductType = false;
        errors[`buyProduct_${index}_type`] = 'This product does not support Piece type. Please remove and select compatible products.';
      }

      if (isValidForProductType && !product.type) {
        errors[`buyProduct_${index}_type`] = 'Please select product type: Box, Piece, or Both';
      }
    });

    if (!formData.slabs || formData.slabs.length === 0) {
      errors.slabs = 'Please add at least one slab configuration';
      return errors;
    }

    const boxRanges = [];
    const pieceRanges = [];
    const packSizeRaw =
      formData.buyProducts.length > 0
        ? formData.buyProducts[0].pack_size ||
        formData.buyProducts[0].unit_per_box ||
        formData.buyProducts[0].pack ||
        1
        : 1;
    const packSize = parseInt(packSizeRaw, 10) > 0 ? parseInt(packSizeRaw, 10) : 1;

    formData.slabs.forEach((slab, index) => {
      if (!slab.slabName || slab.slabName.trim() === '') {
        errors[`slab_${index}_name`] = 'Slab name is required';
      }

      if (slab.boxMinQuantity !== '' || slab.boxMaxQuantity !== '') {
        const boxMin = parseInt(slab.boxMinQuantity);
        const boxMax = parseInt(slab.boxMaxQuantity);

        if (isNaN(boxMin) || isNaN(boxMax)) {
          errors[`slab_${index}_boxRange`] = 'Please enter valid range format (e.g., 10-20)';
        } else if (boxMin >= boxMax) {
          errors[`slab_${index}_boxRange`] = 'Minimum quantity must be less than maximum quantity';
        } else {
          boxRanges.push({ slabIndex: index, min: boxMin, max: boxMax, packSize });
        }
      }

      if (!slab.discountType) {
        errors[`slab_${index}_discountType`] = 'Please select discount type';
      }

      if (slab.discountType === 'set-value') {
        if (buyProductType === 'box-only' || buyProductType === 'both') {
          if (!slab.boxProductsValue || slab.boxProductsValue === '') {
            errors[`slab_${index}_boxValue`] = 'Please enter set value for box products';
          } else {
            const boxValue = parseFloat(slab.boxProductsValue);
            if (isNaN(boxValue)) {
              errors[`slab_${index}_boxValue`] = 'Please enter a valid numeric value';
            } else if (boxValue <= 0) {
              errors[`slab_${index}_boxValue`] = 'Set value for boxes must be greater than 0';
            }
          }
        }

        if (buyProductType === 'piece-only' || buyProductType === 'both') {
          if (!slab.pieceProductsValue || slab.pieceProductsValue === '') {
            errors[`slab_${index}_pieceValue`] = 'Please enter set value for piece products';
          } else {
            const pieceValue = parseFloat(slab.pieceProductsValue);
            if (isNaN(pieceValue)) {
              errors[`slab_${index}_pieceValue`] = 'Please enter a valid numeric value';
            } else if (pieceValue <= 0) {
              errors[`slab_${index}_pieceValue`] = 'Set value for pieces must be greater than 0';
            }
          }
        }
      } else if (slab.discountType === 'percentage' || slab.discountType === 'fixed') {
        if (buyProductType === 'box-only' || buyProductType === 'both') {
          if (!slab.boxProductsValue || slab.boxProductsValue === '') {
            errors[`slab_${index}_boxValue`] = 'Please enter discount value for box products';
          } else {
            const boxValue = parseFloat(slab.boxProductsValue);
            if (isNaN(boxValue)) {
              errors[`slab_${index}_boxValue`] = 'Please enter a valid numeric value';
            } else if (boxValue < 0) {
              errors[`slab_${index}_boxValue`] = 'Discount value cannot be negative';
            } else if (slab.discountType === 'percentage' && boxValue > 100) {
              errors[`slab_${index}_boxValue`] = 'Percentage must be between 0 and 100';
            }
          }
        }

        if (buyProductType === 'piece-only' || buyProductType === 'both') {
          if (!slab.pieceProductsValue || slab.pieceProductsValue === '') {
            errors[`slab_${index}_pieceValue`] = 'Please enter discount value for piece products';
          } else {
            const pieceValue = parseFloat(slab.pieceProductsValue);
            if (isNaN(pieceValue)) {
              errors[`slab_${index}_pieceValue`] = 'Please enter a valid numeric value';
            } else if (pieceValue < 0) {
              errors[`slab_${index}_pieceValue`] = 'Discount value cannot be negative';
            } else if (slab.discountType === 'percentage' && pieceValue > 100) {
              errors[`slab_${index}_pieceValue`] = 'Percentage must be between 0 and 100';
            }
          }
        }
      }

      if (slab.pieceMinQuantity !== '' || slab.pieceMaxQuantity !== '') {
        const pieceMin = parseInt(slab.pieceMinQuantity);
        const pieceMax = parseInt(slab.pieceMaxQuantity);

        if (isNaN(pieceMin) || isNaN(pieceMax)) {
          errors[`slab_${index}_pieceRange`] = 'Please enter a valid piece quantity';
        } else if (pieceMin >= pieceMax) {
          errors[`slab_${index}_pieceRange`] = 'Minimum quantity must be less than maximum quantity';
        } else {
          pieceRanges.push({ slabIndex: index, min: pieceMin, max: pieceMax });
        }
      }

      if (slab.hasGetProducts) {
        if (!slab.getQuantity || slab.getQuantity === '') {
          errors[`slab_${index}_getQuantity`] = 'Get quantity is required and must be a positive number';
        } else {
          const getQty = parseInt(slab.getQuantity);
          if (isNaN(getQty) || getQty < 1) {
            errors[`slab_${index}_getQuantity`] = 'Get quantity must be at least 1';
          } else if (getQty > 9999) {
            errors[`slab_${index}_getQuantity`] = 'Get quantity cannot exceed 9999';
          } else {
            const compulsoryCount = (slab.getProducts || [])
              .filter((p) => p.compulsory)
              .reduce((sum, p) => sum + (parseInt(p.quantity) || 1), 0);
            if (compulsoryCount > getQty) {
              errors[`slab_${index}_getQuantity`] = `Compulsory quantity (${compulsoryCount}) cannot exceed get quantity (${getQty})`;
            }
          }
        }
        if (!slab.getProducts || slab.getProducts.length === 0) {
          errors[`slab_${index}_getProducts`] = 'Please add at least one get product';
        } else {
          slab.getProducts.forEach((product, pIndex) => {
            if (!product.type) {
              errors[`slab_${index}_getProduct_${pIndex}_type`] = 'Please select pricing for get products';
            }
            if (product.pricingType === 'offer' && (!product.offerPrice || parseFloat(product.offerPrice) === 0)) {
              errors[`slab_${index}_getProduct_${pIndex}_offerPrice`] = 'Get products will be free. Please confirm.';
            }
          });
        }
      }
    });

    const overlapMessages = [];

    for (let i = 0; i < boxRanges.length; i++) {
      for (let j = i + 1; j < boxRanges.length; j++) {
        const range1 = boxRanges[i];
        const range2 = boxRanges[j];
        if (
          (range1.min <= range2.max && range1.max >= range2.min) ||
          (range2.min <= range1.max && range2.max >= range1.min)
        ) {
          const slab1Name = formData.slabs[range1.slabIndex]?.slabName || `Slab ${range1.slabIndex + 1}`;
          const slab2Name = formData.slabs[range2.slabIndex]?.slabName || `Slab ${range2.slabIndex + 1}`;
          overlapMessages.push(`Box range [${range1.min}-${range1.max}] in [${slab1Name}] overlaps with [${slab2Name}]`);
        }
      }
    }

    for (let i = 0; i < pieceRanges.length; i++) {
      for (let j = i + 1; j < pieceRanges.length; j++) {
        const range1 = pieceRanges[i];
        const range2 = pieceRanges[j];
        if (
          (range1.min <= range2.max && range1.max >= range2.min) ||
          (range2.min <= range1.max && range2.max >= range1.min)
        ) {
          const slab1Name = formData.slabs[range1.slabIndex]?.slabName || `Slab ${range1.slabIndex + 1}`;
          const slab2Name = formData.slabs[range2.slabIndex]?.slabName || `Slab ${range2.slabIndex + 1}`;
          overlapMessages.push(`Piece range [${range1.min}-${range1.max}] in [${slab1Name}] overlaps with [${slab2Name}]`);
        }
      }
    }

    if (overlapMessages.length > 0) {
      errors.slabOverlap = overlapMessages.length === 1
        ? `${overlapMessages[0]}. Please adjust.`
        : `Multiple overlaps detected: ${overlapMessages.join('; ')}. Please adjust all ranges.`;
    }

    if (formData.slabs && formData.slabs.length > 0) {
      formData.slabs.forEach((slab, index) => {
        if (
          slab.productType === 'both' &&
          slab.boxMinQuantity &&
          slab.boxMaxQuantity &&
          slab.pieceMinQuantity &&
          slab.pieceMaxQuantity
        ) {
          const boxMin = parseInt(slab.boxMinQuantity);
          const boxMax = parseInt(slab.boxMaxQuantity);
          const pieceMin = parseInt(slab.pieceMinQuantity);
          const pieceMax = parseInt(slab.pieceMaxQuantity);
          const packSize =
            formData.buyProducts.length > 0 ? formData.buyProducts[0].pack_size || 1 : 1;

          const boxMinInPieces = boxMin * packSize;
          const boxMaxInPieces = boxMax * packSize;

          if (
            (pieceMin <= boxMaxInPieces && pieceMax >= boxMinInPieces) ||
            (boxMinInPieces <= pieceMax && boxMaxInPieces >= pieceMin)
          ) {
            errors[`slab_${index}_conflict`] =
              `Quantity conflict: ${pieceMin}-${pieceMax} pieces and ${boxMin}-${boxMax} boxes overlap (${boxMin}-${boxMax} boxes = ${boxMinInPieces}-${boxMaxInPieces} pieces based on pack size of ${packSize}). Please adjust quantities.`;
          }
        }
      });
    }

    return errors;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (formData.description.length > 500) newErrors.description = 'Description cannot exceed 500 characters';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (formData.startDate && formData.endDate) {
      const startDay = moment(formData.startDate).startOf('day');
      const endDay = moment(formData.endDate).startOf('day');
      if (endDay.isBefore(startDay)) {
        newErrors.endDate = 'End date cannot be before start date';
      } else {
        const daysDifference = endDay.diff(startDay, 'days');
        if (daysDifference > 365) {
          newErrors.endDate = 'Promotion period cannot exceed 1 year. Please adjust your dates.';
        }
      }
    }
    if (formData.selectedChannels.length === 0)
      newErrors.channels = 'At least one channel is required';

    if (formData.promotionType === 'buy-x-get-y-free') {
      const promotionErrors = validateBuyXGetYFree();
      Object.assign(newErrors, promotionErrors);
    } else if (formData.promotionType === 'buy-x-get-y-discount') {
      const promotionErrors = validateBuyXGetYDiscount();
      Object.assign(newErrors, promotionErrors);
    } else if (formData.promotionType === 'buy-x-discount') {
      const promotionErrors = validateBuyXDiscount();
      Object.assign(newErrors, promotionErrors);
    } else if (formData.promotionType === 'multi-layer-offer') {
      const promotionErrors = validateMultiLayerOffer();
      Object.assign(newErrors, promotionErrors);
    } else if (formData.promotionType === 'group-offer') {
      const promotionErrors = validateGroupOffer();
      Object.assign(newErrors, promotionErrors);
    }

    setErrors(newErrors);
    return newErrors;
  };

  const transformFormDataToAPI = () => {
    const basePayload = {
      title: formData.title,
      description: formData.description,
      type: formData.promotionType,
      start_date: formData.startDate ? moment(formData.startDate).format('YYYY-MM-DD') : '',
      end_date: formData.endDate ? moment(formData.endDate).format('YYYY-MM-DD') : '',
      channels: formData.selectedChannels,
    };

    let config = {};

    if (formData.promotionType === 'buy-x-get-y-free') {
      config = {
        buy_products: formData.buyProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type || 'Box',
          pricing_type: product.pricingType || 'sales',
          is_compulsory: product.compulsory || false,
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
        })),
        get_products: formData.getProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type || 'Box',
          pricing_type: product.pricingType || 'sales',
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
          is_compulsory: product.compulsory || false,
          offer_price: product.offerPrice || 0,
        })),
      };
    } else if (formData.promotionType === 'buy-x-get-y-discount') {

      const getDiscount = {
        discount_config_type: formData.getDiscountType || 'set-value',
        discount_type:
          formData.getDiscountType === 'set-value'
            ? 'set-value'
            : formData.getItemDiscountType || 'percentage',
        discount_value:
          formData.getDiscountType === 'set-value'
            ? parseFloat(formData.getSetValue) || 0
            : parseFloat(formData.getItemDiscountValue) || 0,
        apply_to:
          formData.getDiscountType === 'set-value'
            ? ''
            : (formData.getItemDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN'),
      };



      const buyDiscountConfig = {};

      // Only include buy_discount when discount is actually enabled (not 'none')
      if (formData.buyDiscountApplyTo && formData.buyDiscountApplyTo !== 'none') {
        buyDiscountConfig.buy_discount = {
          apply_to: formData.buyDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN',
          discount_type: formData.buyDiscountType || 'percentage',
          discount_value: parseFloat(formData.buyDiscountValue) || 0,
        };
      }

      config = {
        buy_quantity: parseInt(formData.buyQuantity) || 0,
        get_quantity: parseInt(formData.getQuantity) || 0,
        ...buyDiscountConfig,
        get_discount: getDiscount,
        buy_products: formData.buyProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type,
          pricing_type: product.pricingType || 'sales',
          is_compulsory: product.compulsory || false,
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
          item_discount:
            formData.buyDiscountApplyTo === 'per-item'
              ? {
                discount_type: product.buyItemDiscountType || 'percentage',
                discount_value: parseFloat(product.buyItemDiscountValue) || 0,
              }
              : null,
        })),
        get_products: formData.getProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type,
          pricing_type: product.pricingType || 'sales',
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
          is_compulsory: product.compulsory || false,
          item_discount:
            formData.getItemDiscountApplyTo === 'per-item'
              ? {
                discount_type: product.itemDiscountType || 'percentage',
                discount_value: parseFloat(product.itemDiscountValue) || 0,
              }
              : null,
        })),
      };
    } else if (formData.promotionType === 'buy-x-discount') {
      const buyDiscountConfig = {};

      // Only add buy_discount when buyDiscountConfigType is 'set-value'
      if (formData.buyDiscountConfigType === 'set-value') {
        buyDiscountConfig.buy_discount = {
          discount_config_type: 'set-value',
          discount_type: 'set-value',
          discount_value: parseFloat(formData.buySetValue) || 0,
          apply_to: '',
        };
      } else if (formData.buyDiscountConfigType === 'value-off') {
        // When value-off is selected, use buy_discount with the actual discount type
        buyDiscountConfig.buy_discount = {
          discount_config_type: 'value-off',
          discount_type: formData.buyXDiscountType === 'no-discount' ? 'percentage' : (formData.buyXDiscountType || 'percentage'),
          discount_value: parseFloat(formData.buyXDiscountValue) || 0,
          apply_to: formData.buyXDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN',
        };
      }

      config = {
        buy_quantity: parseInt(formData.buyQuantity) || 0,
        ...buyDiscountConfig,
        buy_products: formData.buyProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type,
          pricing_type: product.pricingType || 'sales',
          is_compulsory: product.compulsory || false,
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
          item_discount:
            formData.buyXDiscountApplyTo === 'per-item'
              ? {
                discount_type: product.buyXItemDiscountType || 'percentage',
                discount_value: parseFloat(product.buyXItemDiscountValue) || 0,
              }
              : null,
        })),
      };
    } else if (formData.promotionType === 'multi-layer-offer') {
      config = {
        product_type: formData.buyProductType || 'box-only',
        buy_products: formData.buyProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type || 'Box',
          box_pricing_type: product.boxPricingType || 'sales',
          piece_pricing_type: product.piecePricingType || 'sales',
          is_compulsory: product.compulsory || false,
        })),
        slabs: (formData.slabs || []).map((slab, index) => ({
          slab_number: index + 1,
          slab_name: slab.slabName,
          slab_order: slab.slabOrder || index + 1,
          product_type: formData.buyProductType || 'box-only',
          box_quantity_range: {
            from: parseInt(slab.boxMinQuantity) || 0,
            to: parseInt(slab.boxMaxQuantity) || 0,
          },
          piece_quantity_range: {
            from: parseInt(slab.pieceMinQuantity) || 0,
            to: parseInt(slab.pieceMaxQuantity) || 0,
          },
          box_pricing_type: 'sales',
          box_discount_type: slab.discountType || 'percentage',
          box_discount_value: parseFloat(slab.boxProductsValue) || 0,
          piece_pricing_type: 'sales',
          piece_discount_type: slab.discountType || 'percentage',
          piece_discount_value: parseFloat(slab.pieceProductsValue) || 0,
          include_get_products: slab.hasGetProducts || false,
          get_quantity: slab.hasGetProducts ? (parseInt(slab.getQuantity) || 0) : 0,
          get_products: slab.hasGetProducts
            ? (slab.getProducts || []).map((product) => ({
              product_id: product.id,
              product_type: product.type || 'Piece',
              quantity: product.quantity || 1,
              pricing_type: product.pricingType || 'sales',
              discount_type: 'free',
              discount_value: 0,
            }))
            : [],
        })),
      };
    } else if (formData.promotionType === 'group-offer') {
      const getDiscount = {
        discount_config_type: formData.getDiscountType || 'set-value',
        discount_type:
          formData.getDiscountType === 'set-value'
            ? 'set-value'
            : formData.getItemDiscountType || 'percentage',
        discount_value:
          formData.getDiscountType === 'set-value'
            ? parseFloat(formData.getSetValue) || 0
            : parseFloat(formData.getItemDiscountValue) || 0,
        apply_to:
          formData.getDiscountType === 'set-value'
            ? ''
            : (formData.getItemDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN'),
      };

      const buyDiscountConfig = {};

      if (formData.buyDiscountApplyTo && formData.buyDiscountApplyTo !== 'none') {
        buyDiscountConfig.buy_discount = {
          apply_to: formData.buyDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN',
          discount_type: formData.buyDiscountType || 'percentage',
          discount_value: parseFloat(formData.buyDiscountValue) || 0,
        };
      }

      const groupIdFromProduct = (product) => {
        const n = parseInt(String(product.groupNo), 10);
        return Number.isFinite(n) && n >= 1 && n <= 10 ? String(n) : null;
      };

      const buyGroupQuantities = formData.buyGroupQuantities || {};
      const getGroupQuantities = formData.getGroupQuantities || {};
      // Group qty is optional; 0 lets the engine fall back to its default of one per group.
      const resolveGroupQty = (groupId, map) => {
        if (!groupId) return 0;
        const qty = parseInt(map[groupId], 10);
        return Number.isFinite(qty) && qty > 0 ? qty : 0;
      };

      config = {
        buy_quantity: parseInt(formData.buyQuantity) || 0,
        get_quantity: parseInt(formData.getQuantity) || 0,
        ...buyDiscountConfig,
        get_discount: getDiscount,
        buy_products: formData.buyProducts.map((product) => {
          const groupId = groupIdFromProduct(product);
          return {
            product_id: String(product.id),
            product_type: product.type,
            pricing_type: product.pricingType || 'sales',
            is_compulsory: product.compulsory || false,
            quantity: resolveGroupQty(groupId, buyGroupQuantities),
            ...(groupId != null ? { group_id: groupId } : {}),
            item_discount:
              formData.buyDiscountApplyTo === 'per-item'
                ? {
                  discount_type: product.buyItemDiscountType || 'percentage',
                  discount_value: parseFloat(product.buyItemDiscountValue) || 0,
                }
                : null,
          };
        }),
        get_products: formData.getProducts.map((product) => {
          const groupId = groupIdFromProduct(product);
          return {
            product_id: String(product.id),
            product_type: product.type,
            pricing_type: product.pricingType || 'sales',
            is_compulsory: product.compulsory || false,
            quantity: resolveGroupQty(groupId, getGroupQuantities),
            ...(groupId != null ? { group_id: groupId } : {}),
            item_discount:
              formData.getItemDiscountApplyTo === 'per-item'
                ? {
                  discount_type: product.itemDiscountType || 'percentage',
                  discount_value: parseFloat(product.itemDiscountValue) || 0,
                }
                : null,
          };
        }),
      };
    }

    return {
      ...basePayload,
      config,
    };
  };

  const checkForZeroPriceProducts = () => {
    const productsWithZeroPrice = [];

    const checkProduct = (product, listType) => {
      const pricingType = product.pricingType || 'sales';
      const price = parseFloat(getProductUnitPrice(product)) || 0;

      if (price === 0) {
        productsWithZeroPrice.push({
          name: product.name,
          code: product.code,
          pricingType,
          listType,
        });
      }
    };

    const promoType = formData.promotionType;

    if (promoType === 'buy-x-discount') {
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
    } else if (promoType === 'buy-x-get-y-discount') {
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
      formData.getProducts?.forEach(product => checkProduct(product, 'Get Products'));
    } else if (promoType === 'buy-x-get-y-free') {
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
    } else if (promoType === 'multi-layer-offer') {
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
    } else if (promoType === 'group-offer') {
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
      formData.getProducts?.forEach(product => checkProduct(product, 'Get Products'));
    }

    return productsWithZeroPrice;
  };

  const handleSave = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      showToast('Please fix all validation errors before submitting', 'error');
      return;
    }

    const zeroPriceProductsList = checkForZeroPriceProducts();
    if (zeroPriceProductsList.length > 0) {
      setZeroPriceProducts(zeroPriceProductsList);
      setShowZeroPriceWarning(true);
      return;
    }

    setSaving(true);
    try {
      const payload = transformFormDataToAPI();

      const response = await updatePromotion(id, payload);

      if (response.success) {
        showToast('Promotion updated successfully!', 'success');
        setTimeout(() => {
          navigate('/Promotions/Promos');
        }, 1500);
      } else {
        let errorMessage = response.message || 'Failed to update promotion';

        if (typeof errorMessage === 'string') {
          errorMessage = errorMessage.replace(/^Validation error:\s*/i, '');
        }

        if (response.errors && Object.keys(response.errors).length > 0) {
          const errorDetails = Object.entries(response.errors)
            .map(([field, message]) => {
              if (typeof message === 'string') {
                return message.replace(/^Validation error:\s*/i, '');
              }
              return message;
            })
            .join(', ');
          errorMessage += ': ' + errorDetails;
        }

        showToast(errorMessage, 'error');
      }
    } catch (error) {
      const errorMsg = error.message || 'Unknown error';
      const cleanErrorMsg = typeof errorMsg === 'string'
        ? errorMsg.replace(/^Validation error:\s*/i, '')
        : errorMsg;
      showToast(cleanErrorMsg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmZeroPriceWarning = async () => {
    setShowZeroPriceWarning(false);
    setZeroPriceProducts([]);
    setSaving(true);
    try {
      const payload = transformFormDataToAPI();

      const response = await updatePromotion(id, payload);

      if (response.success) {
        showToast('Promotion updated successfully!', 'success');
        setTimeout(() => {
          navigate('/Promotions/Promos');
        }, 1500);
      } else {
        let errorMessage = response.message || 'Failed to update promotion';

        if (typeof errorMessage === 'string') {
          errorMessage = errorMessage.replace(/^Validation error:\s*/i, '');
        }

        if (response.errors && Object.keys(response.errors).length > 0) {
          const errorDetails = Object.entries(response.errors)
            .map(([field, message]) => {
              if (typeof message === 'string') {
                return message.replace(/^Validation error:\s*/i, '');
              }
              return message;
            })
            .join(', ');
          errorMessage += ': ' + errorDetails;
        }

        showToast(errorMessage, 'error');
      }
    } catch (error) {
      const errorMsg = error.message || 'Unknown error';
      const cleanErrorMsg = typeof errorMsg === 'string'
        ? errorMsg.replace(/^Validation error:\s*/i, '')
        : errorMsg;
      showToast(cleanErrorMsg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelZeroPriceWarning = () => {
    setShowZeroPriceWarning(false);
    setZeroPriceProducts([]);
  };

  const renderPromotionTypeComponent = () => {
    const commonProps = {
      formData,
      handleInputChange,
      handleOpenProductModal,
      handleRemoveProduct,
      handleProductCompulsoryChange,
      handleProductQuantityChange,
      handleProductTypeChange,
      handleProductPricingChange,
      buyProductsSearch,
      setBuyProductsSearch,
      getProductsSearch,
      setGetProductsSearch,
      handleSlabProductQuantityChange,
      readOnly: isViewMode,
      errors,
    };

    switch (formData.promotionType) {
      case 'buy-x-get-y-free':
        return <BuyXGetYFree {...commonProps} />;
      case 'buy-x-get-y-discount':
        return <BuyXGetYDiscount {...commonProps} />;
      case 'buy-x-discount':
        return <BuyXDiscount {...commonProps} />;
      case 'multi-layer-offer':
        return (
          <MultiLayerOffer
            {...commonProps}
            multiLayerStep={multiLayerStep}
            setMultiLayerStep={setMultiLayerStep}
            setModalType={setModalType}
            setCurrentSlabIndex={setCurrentSlabIndex}
            setModalTitle={setModalTitle}
            setShowProductModal={setShowProductModal}
            handleSlabRemoveProduct={handleSlabRemoveProduct}
            handleSlabProductTypeChange={handleSlabProductTypeChange}
            handleSlabProductPricingChange={handleSlabProductPricingChange}
            handleSlabProductCompulsoryChange={handleSlabProductCompulsoryChange}
            showAllSteps={false}
            readOnly={isViewMode}
          />
        );
      case 'group-offer':
        return (
          <GroupOffer
            {...commonProps}
            handleProductItemDiscountTypeChange={handleProductItemDiscountTypeChange}
            handleProductItemDiscountValueChange={handleProductItemDiscountValueChange}
            readOnly={isViewMode}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div>
 
        <Box
          sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading promotion data...
          </Typography>
        </Box>

      </div>
    );
  }

  if (error) {
    return (
      <div>
  
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button variant="contained"
            onClick={() => navigate('/Promotions/Promos')}>
            Back to Promotions
          </Button>
        </Box>
      </div>
    );
  }

  const handleCancel = () => {
    navigate('/Promotions/Promos');
  };

  return (
    <>
      <Box sx={{ width: "100%", p: 1.5, backgroundColor: "#f5f7fa" }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: "#fff",
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box
              sx={
                isViewMode
                  ? {
                    '& .MuiButtonBase-root:not(.allow-interaction)': {
                      cursor: 'default !important',
                    },
                    '& .MuiButtonBase-root.allow-interaction': {
                      cursor: 'pointer',
                    },
                    '& .allow-interaction, & .allow-interaction *': {
                      userSelect: 'auto',
                    },
                  }
                  : undefined
              }
            >
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Promotion Details
                    </Typography>
                    <Grid container spacing={2} mt={0.5}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          className="input-border"
                          label="Promotion Title"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          error={!!errors.title}
                          helperText={errors.title}
                          placeholder="Enter promotion title"
                          inputProps={{ maxLength: 100 }}
                          InputProps={{ readOnly: isViewMode }}
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 40, // default medium height
                            },
                            
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <DatePicker
                          label="Start Date"
                          className="input-border"
                          value={formData.startDate}
                          onChange={(date) => handleInputChange('startDate', date)}
                          format="DD-MM-YYYY"
                          minDate={moment()}
                          disabled={isViewMode}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.startDate,
                              helperText: errors.startDate,
                              placeholder: 'DD-MM-YYYY',
                              InputProps: { readOnly: isViewMode },
                              sx: {
                                '& .MuiInputBase-root': {
                                  height: 40
                                },
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <DatePicker
                          label="End Date"
                          className="input-border"
                          value={formData.endDate}
                          onChange={(date) => handleInputChange('endDate', date)}
                          format="DD-MM-YYYY"
                          minDate={formData.startDate || moment()}
                          disabled={isViewMode}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.endDate,
                              helperText: errors.endDate,
                              placeholder: 'DD-MM-YYYY',
                              InputProps: { readOnly: isViewMode },
                              sx: {
                                '& .MuiInputBase-root': {
                                  height: 40
                                },
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          // className="input-border"
                          label="Promotion Description"
                          value={formData.description || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value.length <= 500) {
                              handleInputChange('description', value);
                            }
                          }}
                          error={!!errors.description}
                          helperText={
                            errors.description ||
                            `${(formData.description || '').length}/500 characters`
                          }
                          placeholder="Enter customer-facing description"
                          multiline
                          minRows={2}
                          maxRows={5}
                          inputProps={{ maxLength: 500 }}
                          InputProps={{ readOnly: isViewMode }}
                    
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 500 }}>
                          Promotion Channel
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                          {Array.isArray(channels) && channels.length > 0 ? (
                            channels.map((channel) => {
                              const isChecked = channel.isMobileApp
                                ? mobileAppChannelValue && formData.selectedChannels.includes(mobileAppChannelValue)
                                : formData.selectedChannels.includes(channel.value);
                              return (
                                <FormControlLabel
                                  key={channel.id}
                                  control={
                                    <Checkbox
                                      checked={isChecked}
                                      onChange={() => {
                                        if (!isViewMode) handleChannelToggle(channel.value);
                                      }}
                                      disabled={isViewMode}
                                      sx={{
                                        color: '#F8991D',
                                        '&.Mui-checked': {
                                          color: '#F8991D',
                                        },
                                      }}
                                    />
                                  }
                                  label={channel.label}
                                />
                              );
                            })
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Loading channels...
                            </Typography>
                          )}
                        </Stack>
                        {errors.channels && (
                          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                            {errors.channels}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Box>

                  <Box>
                    <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Promotion Type
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1, fontStyle: 'italic' }}
                    >
                      Promotion type cannot be changed after creation to maintain data integrity.
                    </Typography>
                    <Divider color="black" />
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 2,
                        width: '100%',
                        alignItems: 'stretch',
                        mt: 1,
                        gridTemplateColumns: {
                          xs: 'minmax(0, 1fr)',
                          sm: 'repeat(3, minmax(0, 1fr))',
                          md: 'repeat(3, minmax(0, 1fr))',
                          lg: 'repeat(5, minmax(0, 1fr))',
                        },
                      }}
                    >
                      {promotionTypes.map((type) => (
                        <Card
                          key={type.id}
                          elevation={0}
                          sx={{
                            bgcolor: formData.promotionType === type.id ? '#e3f2fd' : '#f5f7fa',
                            cursor: 'default',
                            transition: 'all 0.2s ease-in-out',
                            height: '100%',
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: 0,
                            border:
                              formData.promotionType === type.id
                                ? '2px solid #2196f3'
                                : '1px solid rgba(59, 59, 59, 0.94)',
                          }}
                        >
                          <Box
                            sx={{
                              p: 2,
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'stretch',
                              textAlign: 'left',
                              minHeight: 0,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                lineHeight: 1.4,
                                color: '#212b36',
                                mb: 1,
                              }}
                            >
                              {type.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                lineHeight: 1.5,
                                fontSize: '0.75rem',
                                color: '#212b36',
                                flex: 1,
                              }}
                            >
                              {type.description}
                            </Typography>
                          </Box>
                        </Card>
                      ))}
                    </Box>
                    {errors.promotionType && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {errors.promotionType}
                      </Typography>
                    )}
                  </Box>

                  {formData.promotionType === 'buy-x-get-y-free' && (
                    <BuyXGetYFree
                      formData={formData}
                      handleInputChange={handleInputChange}
                      handleOpenProductModal={handleOpenProductModal}
                      handleRemoveProduct={handleRemoveProduct}
                      handleProductCompulsoryChange={handleProductCompulsoryChange}
                      handleProductQuantityChange={handleProductQuantityChange}
                      handleProductTypeChange={handleProductTypeChange}
                      handleProductPricingChange={handleProductPricingChange}
                      buyProductsSearch={buyProductsSearch}
                      setBuyProductsSearch={setBuyProductsSearch}
                      getProductsSearch={getProductsSearch}
                      setGetProductsSearch={setGetProductsSearch}
                      errors={errors}
                      readOnly={isViewMode}
                      isViewMode={isViewMode}
                    />
                  )}

                  {formData.promotionType === 'buy-x-get-y-discount' && (
                    <BuyXGetYDiscount
                      formData={formData}
                      handleInputChange={handleInputChange}
                      handleOpenProductModal={handleOpenProductModal}
                      handleRemoveProduct={handleRemoveProduct}
                      handleProductCompulsoryChange={handleProductCompulsoryChange}
                      handleProductQuantityChange={handleProductQuantityChange}
                      handleProductTypeChange={handleProductTypeChange}
                      handleProductPricingChange={handleProductPricingChange}
                      handleProductItemDiscountTypeChange={handleProductItemDiscountTypeChange}
                      handleProductItemDiscountValueChange={handleProductItemDiscountValueChange}
                      buyProductsSearch={buyProductsSearch}
                      setBuyProductsSearch={setBuyProductsSearch}
                      getProductsSearch={getProductsSearch}
                      setGetProductsSearch={setGetProductsSearch}
                      errors={errors}
                      readOnly={isViewMode}
                      isViewMode={isViewMode}
                    />
                  )}

                  {formData.promotionType === 'buy-x-discount' && (
                    <BuyXDiscount
                      formData={formData}
                      handleInputChange={handleInputChange}
                      handleOpenProductModal={handleOpenProductModal}
                      handleRemoveProduct={handleRemoveProduct}
                      handleProductCompulsoryChange={handleProductCompulsoryChange}
                      handleProductTypeChange={handleProductTypeChange}
                      handleProductPricingChange={handleProductPricingChange}
                      buyProductsSearch={buyProductsSearch}
                      setBuyProductsSearch={setBuyProductsSearch}
                      errors={errors}
                      readOnly={isViewMode}
                      isViewMode={isViewMode}
                    />
                  )}

                  {formData.promotionType === 'multi-layer-offer' && (
                    <MultiLayerOffer
                      formData={formData}
                      handleInputChange={handleInputChange}
                      handleOpenProductModal={handleOpenProductModal}
                      handleRemoveProduct={handleRemoveProduct}
                      handleProductCompulsoryChange={handleProductCompulsoryChange}
                      handleProductQuantityChange={handleProductQuantityChange}
                      handleProductTypeChange={handleProductTypeChange}
                      handleProductPricingChange={handleProductPricingChange}
                      multiLayerStep={multiLayerStep}
                      setMultiLayerStep={setMultiLayerStep}
                      setModalType={setModalType}
                      setCurrentSlabIndex={setCurrentSlabIndex}
                      setModalTitle={setModalTitle}
                      setShowProductModal={setShowProductModal}
                      handleSlabRemoveProduct={handleSlabRemoveProduct}
                      handleSlabProductTypeChange={handleSlabProductTypeChange}
                      handleSlabProductCompulsoryChange={handleSlabProductCompulsoryChange}
                      buyProductsSearch={buyProductsSearch}
                      setBuyProductsSearch={setBuyProductsSearch}
                      readOnly={isViewMode}
                      isViewMode={isViewMode}
                      errors={errors}
                    />
                  )}

                  {formData.promotionType === 'group-offer' && (
                    <GroupOffer
                      formData={formData}
                      handleInputChange={handleInputChange}
                      handleOpenProductModal={handleOpenProductModal}
                      handleRemoveProduct={handleRemoveProduct}
                      handleProductCompulsoryChange={handleProductCompulsoryChange}
                      handleProductQuantityChange={handleProductQuantityChange}
                      handleProductTypeChange={handleProductTypeChange}
                      handleProductPricingChange={handleProductPricingChange}
                      handleProductItemDiscountTypeChange={handleProductItemDiscountTypeChange}
                      handleProductItemDiscountValueChange={handleProductItemDiscountValueChange}
                      buyProductsSearch={buyProductsSearch}
                      setBuyProductsSearch={setBuyProductsSearch}
                      getProductsSearch={getProductsSearch}
                      setGetProductsSearch={setGetProductsSearch}
                      errors={errors}
                      readOnly={isViewMode}
                    />
                  )}
                  {!isViewMode && (
                    <>
                      <Divider color="black" mt={1} />
                      <Stack direction="row" spacing={2} justifyContent="space-between" mb={4} mt={3}>
                        {formData.promotionType === 'multi-layer-offer' ? (
                          <>
                            <Button variant="outlined" onClick={handleCancel}>
                              Cancel
                            </Button>
                            <Stack direction="row" spacing={2}>
                              {multiLayerStep === 2 && (
                                <Button
                                  variant="outlined"
                                  startIcon={<Iconify icon="eva:arrow-back-fill" />}
                                  onClick={() => setMultiLayerStep(1)}
                                >
                                  Previous
                                </Button>
                              )}
                              {multiLayerStep === 1 ? (
                                <Button
                                  variant="contained"
                                  endIcon={<Iconify icon="eva:arrow-forward-fill" />}
                                  onClick={() => setMultiLayerStep(2)}
                                  sx={{ backgroundColor: '#003DA5' }}
                                >
                                  Next
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  onClick={handleSave}
                                  disabled={saving}
                                  sx={{ backgroundColor: '#003DA5' }}
                                  startIcon={
                                    saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null
                                  }
                                >
                                  {saving ? 'Updating...' : 'Update Promotion'}
                                </Button>
                              )}
                            </Stack>
                          </>
                        ) : (
                          <>
                            <Button variant="outlined" onClick={handleCancel} disabled={saving}>
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={handleSave}
                              disabled={saving}
                              sx={{ backgroundColor: '#003DA5' }}
                              startIcon={
                                saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null
                              }
                            >
                              {saving ? 'Updating...' : 'Update Promotion'}
                            </Button>
                          </>
                        )}
                      </Stack>
                    </>
                  )}

                  {isViewMode && formData.promotionType === 'multi-layer-offer' && (
                    <>
                      <Divider color="black" />
                      <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                          className="allow-interaction"
                          variant="outlined"
                          sx={{
                            backgroundColor: "#F8991D",
                            "&:hover": {
                              backgroundColor: "#F8991D",
                              boxShadow: "none",
                            },
                          }}
                          startIcon={<Iconify icon="eva:arrow-back-fill" />}
                          onClick={handleCancel}
                        >
                          Back to Promotions
                        </Button>
                        {multiLayerStep === 2 && (
                          <Button
                            className="allow-interaction"
                            variant="outlined"
                            startIcon={<Iconify icon="eva:arrow-back-fill" />}
                            onClick={() => setMultiLayerStep(1)}
                          >
                            Previous
                          </Button>
                        )}
                        {multiLayerStep === 1 && (
                          <Button
                            className="allow-interaction"
                            variant="outlined"
                            endIcon={<Iconify icon="eva:arrow-forward-fill" />}
                            onClick={() => setMultiLayerStep(2)}
                            sx={{
                              borderColor: '#F8991D',
                              color: '#F8991D',
                              "&:hover": {
                                backgroundColor: "#F8991D",
                                color: "#fff",
                                boxShadow: "none",
                              },
                            }}
                          >
                            Next
                          </Button>
                        )}
                      </Stack>
                    </>
                  )}
                </Stack>
              </LocalizationProvider>
            </Box>

            {isViewMode && formData.promotionType !== 'multi-layer-offer' && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  variant="contained"
                  className="button-variant-contained"
                  startIcon={<Iconify icon="eva:arrow-back-fill" />}
                  onClick={handleCancel}
                >
                  Back to Promotions
                </Button>
              </Box>
            )}

            {!isViewMode && (
              <ProductSelectionModal
                open={showProductModal}
                onClose={handleCloseProductModal}
                onConfirm={handleConfirmProducts}
                title={modalTitle}
                selectedProducts={
                  modalType === 'getForSlab' && currentSlabIndex !== null
                    ? formData.slabs?.[currentSlabIndex]?.getProducts || []
                    : formData[modalType === 'buy' ? 'buyProducts' : 'getProducts']
                }
                productTypeFilter={
                  formData.promotionType === 'multi-layer-offer' && (modalType === 'buy' || modalType === 'getForSlab')
                    ? formData.buyProductType
                    : null
                }
                firstBuyProduct={
                  formData.promotionType === 'multi-layer-offer' &&
                    modalType === 'buy' &&
                    formData.buyProducts &&
                    formData.buyProducts.length > 0
                    ? formData.buyProducts[0]
                    : null
                }
                isGetForSlab={modalType === 'getForSlab'}
                alreadyAddedProducts={[]}
                showToast={showToast}
              />
            )}

            {!isViewMode && (
              <Dialog
                open={showZeroPriceWarning}
                onClose={handleCancelZeroPriceWarning}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle sx={{ bgcolor: '#FFF4E5', color: '#FF9800', borderBottom: '1px solid #FFE0B2' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Iconify icon="eva:alert-triangle-fill" width={24} />
                    <Typography variant="h6" component="span">
                      Zero Price Warning
                    </Typography>
                  </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Products will be offered for free. Is this correct?
                  </Alert>

                  <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                    The following products have zero pricing:
                  </Typography>

                  <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    {zeroPriceProducts.map((product, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          bgcolor: '#FFF4E5',
                          borderRadius: 1,
                          border: '1px solid #FFE0B2',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#212B36' }}>
                          {product.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#637381' }}>
                          Code: {product.code} | Pricing: {product.pricingType} | Location: {product.listType}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Typography variant="body2" sx={{ mt: 2, color: '#637381' }}>
                    If you proceed, these products will be free in this promotion.
                  </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                  <Button onClick={handleCancelZeroPriceWarning} variant="outlined">
                    Cancel & Review
                  </Button>
                  <Button
                    onClick={handleConfirmZeroPriceWarning}
                    variant="contained"
                    color="warning"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                  >
                    {saving ? 'Updating...' : 'Yes, Proceed'}
                  </Button>
                </DialogActions>
              </Dialog>
            )}

            {!isViewMode && (
              <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={handleCloseToast}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Alert
                  onClose={handleCloseToast}
                  severity={toast.severity}
                  variant="filled"
                  sx={{ width: '100%' }}
                >
                  {toast.message}
                </Alert>
              </Snackbar>
            )}
          </Box>
        </Paper>
      </Box>
    </>
  );
}
